import { fetchAuthSession } from 'aws-amplify/auth';
import * as Sentry from '@sentry/react';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Report server-side and network failures to Sentry.
 *
 * Every caller funnels these through TanStack Query, which catches them into
 * error state — so nothing is ever an unhandled rejection and Sentry's global
 * handlers never see them. That blind spot hid the 2026-07-29 rate timeouts
 * (API Gateway 504s) entirely. 4xx are the user's own doing (validation,
 * expired session) and stay out of Sentry to keep the signal clean.
 */
function reportApiFailure(
  endpoint: string,
  method: string,
  error: unknown,
  statusCode?: number
): void {
  if (statusCode !== undefined && statusCode < 500) return;

  // Not every rejected fetch is a fault. Going offline, closing the tab, or
  // navigating away mid-request all reject with `TypeError: Failed to fetch`.
  // Pages on a refetchInterval would turn that into steady per-user noise and
  // bury the real 5xx this reporting exists to surface.
  if (statusCode === undefined) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    if (error instanceof DOMException && error.name === 'AbortError') return;
    if (error instanceof Error && error.name === 'AbortError') return;
  }

  Sentry.captureException(error, {
    tags: {
      api_endpoint: endpoint,
      api_method: method,
      ...(statusCode !== undefined ? { api_status: String(statusCode) } : {}),
      api_failure: statusCode === undefined ? 'network' : 'server',
    },
    extra: { endpoint, method, statusCode },
  });
}

// Fired when the session is terminally dead (refresh token expired/revoked, or
// no Amplify session at all) so AuthContext can sign the user out and land them
// on the login page with a "session expired" notice — instead of every page
// showing its generic query-error state. Fired only when re-auth is the ONLY
// way forward, never on a 401 that a token refresh might still fix.
export const SESSION_EXPIRED_EVENT = 'arivo:session-expired';

// A dead-session discovery on a page with N parallel queries would otherwise
// fire N events and N racing signOut() calls (a real Cognito revocation call,
// not a local no-op). First notification wins; AuthContext re-arms on login.
let sessionExpiredNotified = false;

export function resetSessionExpiredNotifier(): void {
  sessionExpiredNotified = false;
}

function notifySessionExpired(): void {
  if (sessionExpiredNotified) return;
  sessionExpiredNotified = true;
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

// Cognito exceptions that mean the refresh token itself is dead — re-login is
// the only way forward. Anything else thrown out of fetchAuthSession (most
// commonly a network TypeError while Amplify refreshes a near-expired token)
// leaves a perfectly valid session behind and must NOT trigger a logout.
const TERMINAL_AUTH_ERROR_NAMES = new Set([
  'NotAuthorizedException',
  'TokenRevokedException',
  'RefreshTokenReuseException',
  'UserNotFoundException',
  'PasswordResetRequiredException',
]);

function isTerminalAuthError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // getAuthToken's own throw: no session in the store at all.
  if (error.message === 'Not authenticated') return true;
  return TERMINAL_AUTH_ERROR_NAMES.has(error.name);
}

class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number,
    public details?: Record<string, unknown>,
    public presaleCode?: string,
    public reason?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getAuthToken(forceRefresh = false): Promise<string> {
  const session = await fetchAuthSession({ forceRefresh });
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('Not authenticated');
  }
  return token;
}

/**
 * getAuthToken, but with session-expiry handling: a terminally dead session
 * fires SESSION_EXPIRED_EVENT (sign-out + login-page banner) and surfaces as a
 * typed ApiError so callers never toast a raw untranslated message; transient
 * refresh failures (network blips) pass through untouched.
 */
async function getRequestToken(forceRefresh = false): Promise<string> {
  try {
    return await getAuthToken(forceRefresh);
  } catch (error) {
    if (isTerminalAuthError(error)) {
      notifySessionExpired();
      throw new ApiError('Session expired', 'UNAUTHORIZED', 401);
    }
    throw error;
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  retry = true
): Promise<T> {
  const token = await getRequestToken();
  const method = (options.method || 'GET').toUpperCase();

  const doFetch = (authToken: string) =>
    fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authToken,
        ...options.headers,
      },
    });

  let response: Response;
  try {
    response = await doFetch(token);
  } catch (error) {
    // Network-level failure: offline, DNS, CORS, connection reset.
    // Chrome keeps navigator.onLine=true through wifi wake/sleep blips, so the
    // onLine guard in reportApiFailure misses most of them — a single failed
    // attempt proves nothing. GETs are safe to re-send: retry once and only
    // report if that fails too. Mutations are not (a request that died
    // mid-flight may have been applied) — report those immediately.
    const aborted =
      options.signal?.aborted ||
      (error instanceof Error && error.name === 'AbortError');
    if (method !== 'GET' || aborted) {
      reportApiFailure(endpoint, method, error);
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 1500));
    if (options.signal?.aborted) {
      throw error;
    }
    try {
      response = await doFetch(token);
    } catch (retryError) {
      reportApiFailure(endpoint, method, retryError);
      throw retryError;
    }
  }

  // Handle 401 by refreshing token and retrying once. getRequestToken fires
  // the session-expired flow itself if the refresh token is dead.
  if (response.status === 401 && retry) {
    const newToken = await getRequestToken(true); // Force refresh

    let retryResponse: Response;
    try {
      retryResponse = await doFetch(newToken);
    } catch (error) {
      reportApiFailure(endpoint, method, error);
      throw error;
    }

    if (retryResponse.status === 401) {
      // Even a freshly refreshed token is rejected — dead session.
      notifySessionExpired();
      throw new ApiError('Session expired', 'UNAUTHORIZED', 401);
    }

    let retryJson;
    try {
      retryJson = await retryResponse.json();
    } catch (error) {
      // Non-JSON body — typically an API Gateway / CloudFront error page.
      reportApiFailure(endpoint, method, error, retryResponse.status);
      throw error;
    }

    if (!retryResponse.ok) {
      const error = retryJson.error || {};
      const apiError = new ApiError(
        error.message || 'Request failed',
        error.code || 'UNKNOWN_ERROR',
        retryResponse.status,
        error.details,
        typeof error.presaleCode === 'string' ? error.presaleCode : undefined,
        typeof error.reason === 'string' ? error.reason : undefined
      );
      reportApiFailure(endpoint, method, apiError, retryResponse.status);
      throw apiError;
    }

    return retryJson.data;
  }

  let json;
  try {
    json = await response.json();
  } catch (error) {
    // Non-JSON body — typically an API Gateway / CloudFront error page.
    reportApiFailure(endpoint, method, error, response.status);
    throw error;
  }

  if (!response.ok) {
    const error = json.error || {};
    const apiError = new ApiError(
      error.message || 'Request failed',
      error.code || 'UNKNOWN_ERROR',
      response.status,
      error.details,
      typeof error.presaleCode === 'string' ? error.presaleCode : undefined,
      typeof error.reason === 'string' ? error.reason : undefined
    );
    reportApiFailure(endpoint, method, apiError, response.status);
    throw apiError;
  }

  return json.data;
}

// Profile
export async function getProfile() {
  return request<{
    id: string;
    email: string;
    companyName: string;
    contactName: string;
    phoneNumber: string;
    preferredLanguage: 'en' | 'fr';
    receivePONotifications?: boolean;
    receiveClaimNotifications?: boolean;
    receiveThresholdReport?: boolean;
    /** Effective value: an unset flag is ON for the client's primary contact. */
    receiveReturnDecisions?: boolean;
    enabledModules?: string[];
    /** Present only for multi-store users (>1 assigned Logiwa client) */
    assignedClients?: Array<{ id: string; name: string; enabledModules: string[] }>;
    createdAt: string;
  }>('/profile');
}

export async function updateProfile(data: {
  companyName?: string;
  contactName?: string;
  phoneNumber?: string;
  preferredLanguage?: 'en' | 'fr';
  receivePONotifications?: boolean;
  receiveClaimNotifications?: boolean;
  receiveReturnDecisions?: boolean;
}) {
  return request('/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Addresses
export interface Address {
  id: string;
  clientId?: string; // 'SYSTEM' for ShipTo warehouses
  nickname: string;
  name: string;
  contactName?: string;
  addressLine1: string;
  addressLine2?: string;
  unitNumber?: string;
  city: string;
  region: string;
  country: string;
  postalCode: string;
  phoneNumber: string;
  phoneExtension?: string;
  email?: string;
  residential: boolean;
  signatureRequired?: boolean;
  isDefault: boolean;
  createdAt: string;
}

export async function getAddresses(clientId?: string) {
  const params = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return request<{ addresses: Address[] }>(`/addresses${params}`);
}

export async function createAddress(data: Omit<Address, 'id' | 'createdAt'>) {
  return request<Address>('/addresses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAddress(id: string, data: Omit<Address, 'id' | 'createdAt'>) {
  return request<Address>(`/addresses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAddress(id: string) {
  return request<{ deleted: boolean }>(`/addresses/${id}`, {
    method: 'DELETE',
  });
}

// Rates
export interface AddressInput {
  name: string;
  contactName?: string;
  addressLine1: string;
  addressLine2?: string;
  unitNumber?: string;
  city: string;
  region: string;
  country: string;
  postalCode: string;
  phoneNumber: string;
  phoneExtension?: string;
  email: string;
  residential: boolean;
}

export interface PackageInput {
  weightLb: number;
  lengthIn: number;
  widthIn: number;
  heightIn: number;
  description: string;
}

export interface Rate {
  id: string;
  carrierName: string;
  serviceName: string;
  transitDays: number | null;
  estimatedDelivery: string | null;
  totalCents: number;
  currency: string;
  // Customs duties/taxes + fees included in totalCents AT COST (DDP US rates only)
  customsCents?: number;
  pickupAvailable?: boolean;
}

// Customs declaration for US-destination shipments
export interface CustomsItemInput {
  hsCode: string;
  description: string;
  countryOfOrigin: string; // ISO alpha-2
  numUnits: number;
  unitValueCents: number; // CAD
  weightLb?: number; // TOTAL line weight (all units)
}

export interface CustomsInput {
  // 'shipper' = DDP (duties included in the rate), 'receiver' = DDU (receiver pays on delivery)
  dutiesPaidBy: 'shipper' | 'receiver';
  items: CustomsItemInput[];
}

export async function getRates(data: {
  origin: AddressInput;
  destination: AddressInput;
  packages: PackageInput[];
  shipDate: string;
  signatureRequired?: boolean;
  clientId?: string; // Admin can specify client
  insurance?: {
    coverageAmountCents: number;
  };
  customs?: CustomsInput;
}) {
  return request<{ rates: Rate[]; errors: Array<{ message: string }> }>('/rates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Shipments
export interface ShipmentSummary {
  id: string;
  carrierName: string;
  serviceName: string;
  trackingNumber: string;
  origin: { city: string; region: string };
  destination: { city: string; region: string };
  referenceCode?: string;
  rmaDirection?: 'inbound' | 'outbound';
  totalCents: number;
  currency: string;
  status: string;
  createdAt: string;
  clientName?: string; // Admin only
}

export async function getShipments(options: { cursor?: string; includeCancelled?: boolean; clientId?: string } = {}) {
  const params = new URLSearchParams();
  if (options.cursor) params.set('cursor', options.cursor);
  if (options.includeCancelled) params.set('includeCancelled', 'true');
  if (options.clientId) params.set('clientId', options.clientId);
  const query = params.toString();
  const url = query ? `/shipments?${query}` : '/shipments';
  return request<{ shipments: ShipmentSummary[]; nextCursor?: string }>(url);
}

export type DuplicateWarning =
  | 'customs_not_copied'
  | 'packages_truncated'
  | 'no_packages'
  | 'techship_unavailable'
  | 'package_details_missing';

export interface DuplicateCandidate {
  id: string;
  source: 'techship' | 'cache' | 'arivo';
  orderCode: string | null;
  channelOrderNumber: string | null;
  shippedAt: string | null;
  carrier: string | null;
  service: string | null;
  trackingNumbers: string[];
  /** null = keep the form's current origin (order-only fallback). */
  origin: AddressInput | null;
  destination: AddressInput;
  packages: PackageInput[];
  reference: string;
  warnings: DuplicateWarning[];
  /** Logiwa client the original order belongs to. */
  ownerClientId: string;
  ownerClientName: string | null;
}

/**
 * ShipTo's own Logiwa client: it reships its clients' orders and pays the
 * shipping, so an admin acting as ShipTo can search another client's orders.
 */
export const SHIPTO_LOGIWA_CLIENT_ID = 'fda8815a-0a7d-4b80-9711-17f41dd616cc';

/**
 * Find a shipped order (order #, channel # or tracking #) to prefill New Shipment.
 * `searchClient` ('all' or a client id) is honoured only for an admin acting as ShipTo.
 */
export async function lookupDuplicateOrder(q: string, clientId?: string, searchClient?: string) {
  const params = new URLSearchParams({ q });
  if (clientId) params.set('clientId', clientId);
  if (searchClient) params.set('searchClient', searchClient);
  return request<{ status: 'found' | 'not_found'; candidates: DuplicateCandidate[] }>(
    `/orders/duplicate-lookup?${params.toString()}`,
  );
}

export async function getShipment(id: string) {
  return request<{
    id: string;
    carrierName: string;
    serviceName: string;
    trackingNumber: string;
    transitDays: number | null;
    origin: AddressInput;
    destination: AddressInput;
    packages: PackageInput[];
    pickup: {
      date: string;
      readyAt: string;
      readyUntil: string;
      contactName: string;
      phoneNumber: string;
      confirmationNumber?: string;
    };
    pickupRequested?: boolean;
    carrierSource?: string;
    referenceCode?: string;
    customs?: CustomsInput;
    rmaDirection?: 'inbound' | 'outbound';
    totalCents: number;
    currency: string;
    // 'processing' = carrier accepted, not confirmed yet (no tracking/label);
    // 'failed' = carrier refused it afterwards (failureReason says why)
    status: string;
    failureReason?: string;
    hasLabel?: boolean;
    createdAt: string;
    clientName?: string; // Admin only
  }>(`/shipments/${id}`);
}

export async function createShipment(data: {
  rateId: string;
  origin: AddressInput;
  destination: AddressInput;
  packages: PackageInput[];
  pickup?: {
    date: string;
    readyAt: string;
    readyUntil: string;
    contactName: string;
    phoneNumber: string;
  };
  referenceCode?: string;
  signatureRequired?: boolean;
  saveOriginAddress?: boolean;
  originAddressNickname?: string;
  saveDestinationAddress?: boolean;
  destinationAddressNickname?: string;
  customs?: CustomsInput;
}) {
  return request<{
    id: string;
    carrierName: string;
    serviceName: string;
    trackingNumber: string;
    transitDays: number | null;
    estimatedDelivery: string | null;
    totalCents: number;
    currency: string;
    pickup: {
      date: string;
      readyAt: string;
      readyUntil: string;
      confirmationNumber?: string;
    };
    carrierSource?: string;
    // absent while the carrier is still confirming (status 'processing')
    labelUrl?: string;
    status: string;
    createdAt: string;
  }>('/shipments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function downloadLabel(shipmentId: string): Promise<Blob> {
  const token = await getRequestToken();

  const response = await fetch(`${API_URL}/shipments/${shipmentId}/label`, {
    headers: {
      Authorization: token,
      Accept: 'application/pdf', // Required for API Gateway binary response
    },
  });

  // Handle 401 by refreshing token and retrying once
  if (response.status === 401) {
    try {
      const newToken = await getRequestToken(true);
      const retryResponse = await fetch(`${API_URL}/shipments/${shipmentId}/label`, {
        headers: {
          Authorization: newToken,
          Accept: 'application/pdf',
        },
      });

      if (!retryResponse.ok) {
        throw new Error('Failed to download label');
      }

      return retryResponse.blob();
    } catch {
      throw new ApiError('Session expired', 'UNAUTHORIZED', 401);
    }
  }

  if (!response.ok) {
    throw new Error('Failed to download label');
  }

  return response.blob();
}

export async function updateShipmentReference(shipmentId: string, referenceCode: string): Promise<{ message: string; referenceCode: string }> {
  return request<{ message: string; referenceCode: string }>(`/shipments/${shipmentId}`, {
    method: 'PATCH',
    body: JSON.stringify({ referenceCode }),
  });
}

export async function cancelShipment(shipmentId: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/shipments/${shipmentId}`, {
    method: 'DELETE',
  });
}

// ============ REPORTS ============

export async function exportShipments(params: {
  startDate?: string;
  endDate?: string;
  clientId?: string;
  status?: string;
  lang?: string;
}): Promise<Blob> {
  const token = await getRequestToken();

  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.set('startDate', params.startDate);
  if (params.endDate) searchParams.set('endDate', params.endDate);
  if (params.clientId) searchParams.set('clientId', params.clientId);
  if (params.status) searchParams.set('status', params.status);
  if (params.lang) searchParams.set('lang', params.lang);

  const queryString = searchParams.toString();
  const url = `${API_URL}/reports/export${queryString ? `?${queryString}` : ''}`;

  const doFetch = (authToken: string) =>
    fetch(url, {
      headers: {
        Authorization: authToken,
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });

  // First attempt. A 401 means a stale token; a thrown error means the response
  // was unreadable (e.g. a cross-origin auth failure that surfaces as a network
  // TypeError rather than a 401 Response). Both recover the same way: force a
  // token refresh and retry exactly once.
  let response = await doFetch(token).catch(() => undefined);
  if (!response || response.status === 401) {
    response = await doFetch(await getRequestToken(true));
  }

  if (!response.ok) {
    throw new Error('Failed to export shipments');
  }

  return response.blob();
}

// ============ ADMIN ENDPOINTS ============

export type MarkupType = 'percentage' | 'flat-fee' | 'both';

export interface AdminUser {
  id: string;
  email: string;
  companyName: string;
  contactName: string;
  phoneNumber: string;
  markupType: MarkupType;
  markupPercent: number;
  markupFlatFeeCents?: number;
  logiwaClientIdentifier?: string;
  logiwaClientName?: string;
  logiwaClientIdentifiers?: string[];
  logiwaClientNames?: Record<string, string>;
  preferredLanguage: 'en' | 'fr';
  active: boolean;
  receivePONotifications?: boolean;
  receiveClaimNotifications?: boolean;
  receiveThresholdReport?: boolean;
  lastSeenAt?: string;
  createdAt: string;
  updatedAt: string;
}

export async function adminGetUsers(cursor?: string) {
  const url = cursor ? `/admin/users?cursor=${encodeURIComponent(cursor)}` : '/admin/users';
  return request<{ users: AdminUser[]; nextCursor?: string }>(url);
}

export async function adminGetUser(userId: string) {
  const response = await request<{ user: AdminUser }>(`/admin/users/${userId}`);
  return response.user;
}

export async function adminCreateUser(data: {
  email: string;
  companyName: string;
  contactName: string;
  phoneNumber: string;
  logiwaClientIdentifiers?: string[];
  preferredLanguage: 'en' | 'fr';
  receivePONotifications?: boolean;
  receiveClaimNotifications?: boolean;
  receiveThresholdReport?: boolean;
}) {
  return request<AdminUser>('/admin/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface AdminUpdateResponse {
  user?: AdminUser;
  message?: string;
  emailFailed?: boolean;
  tempPassword?: string;
}

export async function adminUpdateUser(
  userId: string,
  data: {
    companyName?: string;
    contactName?: string;
    phoneNumber?: string;
    logiwaClientIdentifiers?: string[];
    preferredLanguage?: 'en' | 'fr';
    active?: boolean;
    resetPassword?: boolean;
    receivePONotifications?: boolean;
    receiveClaimNotifications?: boolean;
    receiveThresholdReport?: boolean;
  }
): Promise<AdminUpdateResponse> {
  return request<AdminUpdateResponse>(`/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function adminDeleteUser(userId: string) {
  return request<{ message: string }>(`/admin/users/${userId}`, {
    method: 'DELETE',
  });
}

// ============ ADMIN CLIENTS (Logiwa clients with enabled/disabled status) ============

export type CarrierGroup = 'ups' | 'canpar' | 'fedex' | 'purolator' | 'gls' | 'loomis' | 'ics' | 'canadapost' | 'obibox' | 'uniuni_qc_on' | 'uniuni_other' | 'nationex' | 'usps' | 'postnl' | 'apc' | 'stallion';

export interface CarrierMargin {
  minCents: number;
  percent: number;
}

export interface AdminClient {
  id: string;
  name: string;
  enabled: boolean;
  rmaEnabled?: boolean;
  replenishmentEnabled?: boolean;

  productsEnabled?: boolean;
  ordersEnabled?: boolean;
  warehousingEnabled?: boolean;
  rmaPrefix?: string;
  hasCustomMargins?: boolean;
  disabledCarriers?: string[];
}

interface AdminClientDetail extends AdminClient {
  rmaLogoS3Key?: string;
  appLogoS3Key?: string;
  orderTrackingEnabled?: boolean;
  linkedPoEditEnabled?: boolean;
  poPrefix?: string;
  soPrefix?: string;
  defaultSoType?: string;
  enabledSoTypes?: string[];
  companyAddress?: string;
  companyCity?: string;
  companyProvince?: string;
  companyPostalCode?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyContactName?: string;
  settings: {
    disabledCarriers: string[];
    carrierMargins: Record<CarrierGroup, CarrierMargin>;
    updatedAt?: string;
    updatedBy?: string;
  };
}

export const DEFAULT_CARRIER_MARGINS: Record<CarrierGroup, CarrierMargin> = {
  ups: { minCents: 100, percent: 12 },
  canpar: { minCents: 100, percent: 12 },
  fedex: { minCents: 100, percent: 12 },
  purolator: { minCents: 100, percent: 12 },
  gls: { minCents: 100, percent: 12 },
  loomis: { minCents: 100, percent: 12 },
  ics: { minCents: 100, percent: 12 },
  canadapost: { minCents: 100, percent: 12 },
  obibox: { minCents: 100, percent: 16 },
  uniuni_qc_on: { minCents: 350, percent: 40 },
  uniuni_other: { minCents: 400, percent: 60 },
  nationex: { minCents: 100, percent: 12 },
  usps: { minCents: 100, percent: 12 },
  postnl: { minCents: 100, percent: 12 },
  apc: { minCents: 100, percent: 12 },
  stallion: { minCents: 100, percent: 12 },
};

export const CARRIER_GROUP_INFO: Record<CarrierGroup, { nameKey: string; descriptionKey: string }> = {
  ups: { nameKey: 'admin.carriers.ups', descriptionKey: 'admin.carriers.upsDescription' },
  canpar: { nameKey: 'admin.carriers.canpar', descriptionKey: 'admin.carriers.canparDescription' },
  fedex: { nameKey: 'admin.carriers.fedex', descriptionKey: 'admin.carriers.fedexDescription' },
  purolator: { nameKey: 'admin.carriers.purolator', descriptionKey: 'admin.carriers.purolatorDescription' },
  gls: { nameKey: 'admin.carriers.gls', descriptionKey: 'admin.carriers.glsDescription' },
  loomis: { nameKey: 'admin.carriers.loomis', descriptionKey: 'admin.carriers.loomisDescription' },
  ics: { nameKey: 'admin.carriers.ics', descriptionKey: 'admin.carriers.icsDescription' },
  canadapost: { nameKey: 'admin.carriers.canadapost', descriptionKey: 'admin.carriers.canadapostDescription' },
  obibox: { nameKey: 'admin.carriers.obibox', descriptionKey: 'admin.carriers.obiboxDescription' },
  uniuni_qc_on: { nameKey: 'admin.carriers.uniuniQcOn', descriptionKey: 'admin.carriers.uniuniQcOnDescription' },
  uniuni_other: { nameKey: 'admin.carriers.uniuniOther', descriptionKey: 'admin.carriers.uniuniOtherDescription' },
  nationex: { nameKey: 'admin.carriers.nationex', descriptionKey: 'admin.carriers.nationexDescription' },
  usps: { nameKey: 'admin.carriers.usps', descriptionKey: 'admin.carriers.uspsDescription' },
  postnl: { nameKey: 'admin.carriers.postnl', descriptionKey: 'admin.carriers.postnlDescription' },
  apc: { nameKey: 'admin.carriers.apc', descriptionKey: 'admin.carriers.apcDescription' },
  stallion: { nameKey: 'admin.carriers.stallion', descriptionKey: 'admin.carriers.stallionDescription' },
};

export async function adminGetClients() {
  return request<{ clients: AdminClient[] }>('/admin/clients');
}

export async function adminGetClient(clientId: string) {
  return request<AdminClientDetail>(`/admin/clients/${encodeURIComponent(clientId)}`);
}

export async function adminUpdateClient(
  clientId: string,
  data: {
    enabled?: boolean;
    clientName?: string;
    rmaEnabled?: boolean;
    rmaPrefix?: string;
    rmaLogoS3Key?: string;
    appLogoS3Key?: string;
    replenishmentEnabled?: boolean;
  
    productsEnabled?: boolean;
    ordersEnabled?: boolean;
    warehousingEnabled?: boolean;
    orderTrackingEnabled?: boolean;
    linkedPoEditEnabled?: boolean;
    poPrefix?: string;
    soPrefix?: string;
    defaultSoType?: string;
    companyContactName?: string;
    companyAddress?: string;
    companyCity?: string;
    companyProvince?: string;
    companyPostalCode?: string;
    companyPhone?: string;
    companyEmail?: string;
    disabledCarriers?: string[];
    carrierMargins?: Partial<Record<CarrierGroup, CarrierMargin>>;
    resetToDefaults?: boolean;
  }
) {
  return request<{ id: string; enabled?: boolean; message: string }>(`/admin/clients/${encodeURIComponent(clientId)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Get presigned URL for uploading a client's RMA logo
export async function getLogoUploadUrl(clientId: string, contentType: string) {
  return request<{ uploadUrl: string; s3Key: string }>(`/admin/clients/${encodeURIComponent(clientId)}/logo-upload-url`, {
    method: 'POST',
    body: JSON.stringify({ contentType }),
  });
}

// Get a presigned POST (type + 512 KB pinned by policy) for a client's gray app logo
export async function getAppLogoUploadPost(clientId: string, contentType: string, size: number) {
  return request<{ uploadUrl: string; fields: Record<string, string>; s3Key: string; maxBytes: number }>(`/admin/clients/${encodeURIComponent(clientId)}/logo-upload-url`, {
    method: 'POST',
    body: JSON.stringify({ purpose: 'app', contentType, size }),
  });
}

// Get presigned URL for displaying a client's RMA logo
export async function getLogoDisplayUrl(clientId: string, s3Key: string) {
  return request<{ url: string }>(`/admin/clients/${encodeURIComponent(clientId)}/logo-upload-url?s3Key=${encodeURIComponent(s3Key)}`);
}

// Get only enabled clients (for dropdowns)
export async function adminGetEnabledClients() {
  const result = await adminGetClients();
  return { clients: result.clients.filter(c => c.enabled) };
}

// Get only clients with RMA enabled (fast DynamoDB-only endpoint)
export async function adminGetRmaEnabledClients() {
  return request<{ clients: Array<{ id: string; name: string; rmaPrefix?: string }> }>('/admin/rma/clients');
}

// ============ ADMIN ADDRESSES ============

export interface AdminAddress extends Address {
  clientName?: string;
}

export interface ImportError {
  row: number;
  reason: string;
}

export interface ImportResult {
  imported: number;
  updated: number;
  failed: ImportError[];
}

export async function adminGetAddresses(clientId?: string) {
  const params = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return request<{ addresses: AdminAddress[] }>(`/admin/addresses${params}`);
}

export async function adminCreateAddress(data: Omit<AdminAddress, 'id' | 'createdAt' | 'clientName'>) {
  return request<AdminAddress>('/admin/addresses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function adminUpdateAddress(id: string, data: Omit<AdminAddress, 'id' | 'createdAt' | 'clientName'>) {
  return request<AdminAddress>(`/admin/addresses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function adminDeleteAddress(id: string) {
  return request<{ deleted: boolean }>(`/admin/addresses/${id}`, {
    method: 'DELETE',
  });
}

export async function adminImportAddresses(file: File): Promise<ImportResult> {
  // Explicit APIGW resource (no {proxy+}), so the presign step shares the route.
  const s3Key = await stageUpload('/admin/addresses/import', file, { action: 'upload-url' });
  const token = await getRequestToken();

  const response = await fetch(`${API_URL}/admin/addresses/import`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify({ s3Key }),
  });

  const json = await response.json();

  if (!response.ok) {
    const error = json.error || {};
    throw new Error(error.message || 'Import failed');
  }

  return json.data;
}

export async function adminExportAddresses(clientId?: string): Promise<Blob> {
  const token = await getRequestToken();
  const params = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';

  const response = await fetch(`${API_URL}/admin/addresses/export${params}`, {
    headers: {
      Authorization: token,
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to export addresses');
  }

  return response.blob();
}

export async function adminGetAddressTemplate(): Promise<Blob> {
  const token = await getRequestToken();

  const response = await fetch(`${API_URL}/admin/addresses/template`, {
    headers: {
      Authorization: token,
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download template');
  }

  return response.blob();
}

// ============ CLIENT ADDRESS IMPORT/EXPORT ============

export async function importAddresses(file: File): Promise<ImportResult> {
  // Explicit APIGW resource (no {proxy+}), so the presign step shares the route.
  const s3Key = await stageUpload('/addresses/import', file, { action: 'upload-url' });
  const token = await getRequestToken();

  const response = await fetch(`${API_URL}/addresses/import`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify({ s3Key }),
  });

  const json = await response.json();

  if (!response.ok) {
    const error = json.error || {};
    throw new Error(error.message || 'Import failed');
  }

  return json.data;
}

export async function exportAddresses(): Promise<Blob> {
  const token = await getRequestToken();

  const response = await fetch(`${API_URL}/addresses/export`, {
    headers: {
      Authorization: token,
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to export addresses');
  }

  return response.blob();
}

export async function getAddressTemplate(): Promise<Blob> {
  const token = await getRequestToken();

  const response = await fetch(`${API_URL}/addresses/template`, {
    headers: {
      Authorization: token,
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download template');
  }

  return response.blob();
}

// ============ RMA TYPES ============

export type RMAStatus =
  | 'created'
  | 'awaiting_receiving'
  | 'received'
  | 'in_service'
  | 'completed'
  | 'awaiting_vendor'
  | 'shipped'
  | 'closed'
  | 'cancelled';

export type ResolutionType =
  | 'repaired'
  | 'replaced'
  | 'refunded'
  | 'unrepairable'
  | 'restocked'
  | 'other';

export interface RMAAddress {
  company: string;
  contact: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
}

export interface RMAReturnTo {
  addressId?: string;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
}

export interface ServiceNote {
  id: string;
  text: string;
  createdAt: string;
  createdBy: string;
}

interface RMAListItem {
  rmaId: string;
  rmaNumber: string;
  status: RMAStatus;
  returnFrom: {
    company: string;
    contact: string;
  };
  product: string;
  serialNumber?: string;
  returnType: string;
  trackingNumber?: string;
  createdAt: string;
  logiwaClientId?: string;
  clientName?: string;
}

export interface RMADetail {
  rmaId: string;
  rmaNumber: string;
  status: RMAStatus;
  statusHistory: Array<{
    status: RMAStatus;
    timestamp: string;
    changedBy: string;
    notes?: string;
  }>;
  returnFrom: RMAAddress;
  returnTo: RMAReturnTo;
  product: string;
  serialNumber?: string;
  problemDescription: string;
  returnType: string;
  inboundShipment?: {
    trackingNumber: string;
    carrierName: string;
    serviceName?: string;
    shipmentId?: string;
    labelS3Key?: string;
    totalCents?: number;
    currency?: string;
    transitDays?: number | null;
    shipDate?: string;
    trackingStatus?: string;
    trackingSlug?: string;
  };
  outboundShipment?: {
    trackingNumber: string;
    carrierName: string;
    serviceName?: string;
    shipmentId?: string;
    labelS3Key?: string;
    totalCents?: number;
    currency?: string;
    transitDays?: number | null;
    shipDate?: string;
    trackingStatus?: string;
    trackingSlug?: string;
  };
  // A label booked while the carrier is still confirming (no tracking yet)
  pendingShipment?: { direction: 'inbound' | 'outbound'; bookedAt: string };
  receivedAt?: string;
  receivingNotes?: string;
  technician?: string;
  workDone?: string;
  resolutionType?: ResolutionType;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  // Available actions as array
  availableActions?: string[];
  // Email history
  emailsSent?: Array<{
    type: string;
    sentAt: string;
    recipient?: string;
  }>;
  // Service notes timeline
  serviceNotes?: ServiceNote[];
  // Admin-only
  logiwaClientId?: string;
  internalNotes?: string;
}

interface CreateRMARequest {
  returnFrom: RMAAddress;
  returnToAddressId: string;
  product: string;
  serialNumber?: string;
  problemDescription: string;
  returnType: string;
  internalNotes?: string;
  lang?: 'en' | 'fr';
  // Admin: create RMA for specific client
  clientId?: string;
}

interface UpdateRMARequest {
  status?: RMAStatus;
  technician?: string;
  workDone?: string;
  resolutionType?: ResolutionType;
  internalNotes?: string;
  statusNotes?: string;
  serviceNote?: string;
  deleteServiceNoteId?: string;
  sendToEmail?: string;
}

// ============ RMA API FUNCTIONS ============

export async function listRMAs(options: { status?: RMAStatus } = {}) {
  const allRmas: RMAListItem[] = [];
  let cursor: string | undefined;
  do {
    const params = new URLSearchParams();
    params.set('limit', '100');
    if (options.status) params.set('status', options.status);
    if (cursor) params.set('cursor', cursor);
    const query = params.toString();
    const result = await request<{ rmas: RMAListItem[]; nextCursor?: string }>(`/rma?${query}`);
    allRmas.push(...result.rmas);
    cursor = result.nextCursor;
  } while (cursor);
  return { rmas: allRmas };
}

export async function getRMA(id: string) {
  return request<RMADetail>(`/rma/${id}`);
}

export async function createRMA(data: CreateRMARequest) {
  return request<{
    rmaId: string;
    rmaNumber: string;
    status: RMAStatus;
    createdAt: string;
  }>('/rma', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function sendRMAEmail(id: string, email: string) {
  return request<{ rmaId: string; rmaNumber: string; status: RMAStatus; updatedAt: string }>(`/rma/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ sendToEmail: email }),
  });
}

export async function updateRMA(id: string, data: UpdateRMARequest) {
  return request<{
    rmaId: string;
    rmaNumber: string;
    status: RMAStatus;
    updatedAt: string;
  }>(`/rma/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function getRMAShippingRates(rmaId: string, data: {
  direction: 'inbound' | 'outbound';
  packages: PackageInput[];
  origin?: Partial<AddressInput>;
  destination?: Partial<AddressInput>;
  shipDate?: string;
}) {
  return request<{ rates: Rate[]; errors: Array<{ message: string }> }>(`/rma/${rmaId}/shipping-rates`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function bookRMAShipment(rmaId: string, data: {
  direction: 'inbound' | 'outbound';
  rateId: string;
}) {
  return request<{
    rmaId: string;
    rmaNumber: string;
    direction: 'inbound' | 'outbound';
    // null while the carrier is still confirming (status 'processing')
    trackingNumber: string | null;
    carrierName: string;
    serviceName: string;
    totalCents: number;
    currency: string;
    transitDays: number | null;
    status: RMAStatus | 'processing';
  }>(`/rma/${rmaId}/book-shipment`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function receiveRMA(rmaId: string, receivingNotes?: string) {
  return request<{
    rmaId: string;
    rmaNumber: string;
    status: RMAStatus;
    receivedBy: string;
    receivedAt: string;
  }>(`/rma/${rmaId}/receive`, {
    method: 'POST',
    body: JSON.stringify(receivingNotes ? { receivingNotes } : {}),
  });
}

// Get RMA PDF presigned download URL (slip, scan label, or completion report)
export async function getRMAPdfUrl(rmaId: string, type: 'slip' | 'scan-label' | 'completion-report') {
  return request<{ url: string }>(`/rma/${encodeURIComponent(rmaId)}/pdf?type=${type}`);
}

export async function lookupRMA(identifier: string) {
  return request<{
    rmaId: string;
    rmaNumber: string;
    status: RMAStatus;
    product: string;
    serialNumber?: string;
    returnFrom: {
      company: string;
      contact: string;
      city: string;
      region: string;
    };
    returnType: string;
    canReceive: boolean;
  }>(`/rma/lookup/${encodeURIComponent(identifier)}`);
}

// ============ ADMIN RMA FUNCTIONS ============

export async function adminListRMAs(options: { status?: RMAStatus; clientId?: string } = {}) {
  const params = new URLSearchParams();
  if (options.status) params.set('status', options.status);
  if (options.clientId) params.set('clientId', options.clientId);
  const query = params.toString();
  const url = query ? `/admin/rma?${query}` : '/admin/rma';
  return request<{ rmas: RMAListItem[] }>(url);
}

export async function getRMASettings(clientId?: string) {
  return request<{ defaultReturnToAddressId: string }>(withClientId('/rma/settings', clientId));
}

export async function adminGetRMASettings(clientId: string) {
  return request<{
    logiwaClientId: string;
    prefix: string;
    nextNumber: number;
    currentYear: number;
    returnTypes: string[];
    defaultReturnToAddressId: string;
    emailNotifications: boolean;
    updatedAt?: string;
    updatedBy?: string;
    isDefault?: boolean;
  }>(`/admin/rma/settings/${encodeURIComponent(clientId)}`);
}

export async function adminUpdateRMASettings(
  clientId: string,
  data: {
    prefix?: string;
    returnTypes?: string[];
    defaultReturnToAddressId?: string;
    emailNotifications?: boolean;
  }
) {
  return request<{
    logiwaClientId: string;
    prefix: string;
    returnTypes: string[];
    defaultReturnToAddressId: string;
    emailNotifications: boolean;
    updatedAt: string;
    updatedBy?: string;
  }>(`/admin/rma/settings/${encodeURIComponent(clientId)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ============ CLAIMS ============

export type ClaimStatus = 'new' | 'waiting_on_client' | 'filed_with_carrier' | 'decision' | 'resolved';
export const isActiveClaimStatus = (status: string): boolean =>
  status === 'new' || status === 'waiting_on_client' || status === 'filed_with_carrier' || status === 'decision';
export type ClaimType = 'lost' | 'damaged';
export type ClaimResolutionType = 'approved' | 'denied' | 'partial';

export interface ClaimRecipient {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
}

/** Admin correction of an open claim's details: changed fields only; null / '' clears. */
export interface ClaimDetailsEdit {
  claimType?: ClaimType;
  claimedAmount?: number | null;
  description?: string;
  storeOrderNumber?: string;
  trackingNumber?: string;
  carrier?: string;
  shipDate?: string;
  affectedProducts?: ClaimAffectedProduct[];
}

export interface ClaimAffectedProduct {
  sku: string;
  name: string;
  orderedQty: number;
  affectedQty: number;
}

export interface ClaimDocument {
  id: string;
  type: 'invoice' | 'client_invoice' | 'damage_photo' | 'packaging_photo' | 'other';
  filename: string;
  s3Key: string;
  contentType: string;
  uploadedAt: string;
  url?: string | null;
  /** The S3 object is gone (legacy pre-recorded upload that never landed). */
  missing?: boolean;
}

export type ClaimDocumentType = ClaimDocument['type'];

interface ClaimResolution {
  type: ClaimResolutionType;
  reimbursedAmount?: number;
  reason?: string;
}

export interface ClaimNote {
  id: string;
  text: string;
  author: string;
  /** Public notes: who wrote it. Absent on legacy notes (all ShipTo). */
  authorRole?: 'client' | 'shipto';
  createdAt: string;
}

export interface ClaimStatusHistoryEntry {
  status: ClaimStatus;
  at: string;
  by: string;
}

interface ClaimActivityEntry {
  action: 'created' | 'status_changed' | 'internal_note' | 'public_update' | 'document_added' | 'decision' | 'resolved' | 'reopened' | 'resolution_edited' | 'details_edited' | 'email_sent';
  details: string;
  actor: string;
  timestamp: string;
}

export interface ClaimListItem {
  claimId: string;
  claimNumber: string;
  claimType: ClaimType;
  status: ClaimStatus;
  storeOrderNumber: string;
  trackingNumber?: string;
  carrier?: string;
  createdAt: string;
  updatedAt: string;
  claimedAmount?: number;
  carrierClaimReference?: string;
  clientReplied?: boolean;
  logiwaClientId?: string;
  clientName?: string;
  statusHistory?: ClaimStatusHistoryEntry[];
  resolutionType?: ClaimResolutionType;
}

export interface ClaimDetail {
  claimId: string;
  claimNumber: string;
  claimType: ClaimType;
  description: string;
  status: ClaimStatus;
  claimedAmount?: number;
  statusBeforeWaiting?: ClaimStatus;
  clientReplied?: boolean;
  clientRepliedAt?: string;
  statusHistory?: ClaimStatusHistoryEntry[];
  createdByRole?: 'client' | 'admin';
  storeOrderNumber: string;
  trackingNumber?: string;
  carrier?: string;
  service?: string;
  shipDate?: string;
  deliveryDate?: string;
  deliveryStatus?: string;
  recipient?: ClaimRecipient;
  packageWeight?: number;
  packageDimensions?: { length: number; width: number; height: number };
  manualEntry?: boolean;
  affectedProducts: ClaimAffectedProduct[];
  documents: ClaimDocument[];
  recipientConfirmedNonReceipt?: boolean;
  carrierClaimReference?: string;
  inspectionRequested?: boolean;
  packagingRetained?: boolean;
  resolution?: ClaimResolution;
  internalNotes?: ClaimNote[];
  publicUpdates: ClaimNote[];
  activityLog: ClaimActivityEntry[];
  logiwaClientId?: string;
  /** Admin get only. */
  clientName?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface ClaimShipmentMatch {
  referenceCode: string;
  trackingNumber?: string;
  carrier?: string;
  service?: string;
  shipDate?: string;
  recipient?: ClaimRecipient;
  packageWeight?: number;
  packageDimensions?: { length: number; width: number; height: number };
  items?: Array<{ sku: string; name: string; quantity: number }>;
}

export interface CreateClaimRequest {
  claimType: ClaimType;
  /** Total claimed, CAD, at most 2 decimals. */
  claimedAmount?: number;
  description: string;
  storeOrderNumber: string;
  trackingNumber?: string;
  carrier?: string;
  service?: string;
  shipDate?: string;
  deliveryDate?: string;
  deliveryStatus?: string;
  recipient?: ClaimRecipient;
  packageWeight?: number;
  packageDimensions?: { length: number; width: number; height: number };
  manualEntry?: boolean;
  affectedProducts: ClaimAffectedProduct[];
  recipientConfirmedNonReceipt?: boolean;
  clientId?: string; // Admin only: create claim on behalf of a client
}

// Claims - Client endpoints
export async function listClaims(params: { status?: ClaimStatus; type?: ClaimType; limit?: number; cursor?: string; clientId?: string } = {}) {
  const searchParams = new URLSearchParams();
  if (params.clientId) searchParams.set('clientId', params.clientId);
  if (params.status) searchParams.set('status', params.status);
  if (params.type) searchParams.set('type', params.type);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.cursor) searchParams.set('cursor', params.cursor);
  const query = searchParams.toString();
  return request<{ claims: ClaimListItem[]; nextCursor?: string }>(query ? `/claims?${query}` : '/claims');
}

export async function getClaim(id: string) {
  return request<ClaimDetail>(`/claims/${id}`);
}

export async function createClaim(data: CreateClaimRequest) {
  return request<{ claimId: string; claimNumber: string; claimType: ClaimType; status: ClaimStatus; createdAt: string }>('/claims', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateClaim(id: string, data: {
  status?: ClaimStatus;
  /** reopen: resolved → filed_with_carrier (new without a carrier ref). edit_resolution: verdict/amount/notes of a decided or resolved claim. */
  action?: 'reopen' | 'edit_resolution';
  clearClientReplied?: boolean;
  carrierClaimReference?: string;
  inspectionRequested?: boolean;
  packagingRetained?: boolean;
  internalNote?: string;
  publicUpdate?: string;
  resolution?: ClaimResolution;
  details?: ClaimDetailsEdit;
  notifyClient?: boolean;
}, clientId?: string) {
  return request<{ claimId: string; claimNumber: string; status: ClaimStatus; updatedAt: string }>(`/claims/${id}${claimClientQs(clientId)}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/** Staged upload, step 1: a presigned PUT URL. Records nothing until confirmed. */
export async function getClaimDocumentUploadUrl(claimId: string, data: {
  contentType: string;
  filename: string;
  documentType: ClaimDocumentType;
}, clientId?: string) {
  return request<{ uploadUrl: string; documentId: string; s3Key: string }>(`/claims/${claimId}/documents${claimClientQs(clientId)}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Staged upload, step 2: records the document once the backend sees it in S3. */
export async function confirmClaimDocument(claimId: string, data: {
  s3Key: string;
  documentId: string;
  documentType: ClaimDocumentType;
  filename: string;
  contentType: string;
}, clientId?: string) {
  return request<{ document: ClaimDocument; clientReplied?: boolean }>(`/claims/${claimId}/documents/confirm${claimClientQs(clientId)}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Upload one file to a claim: upload URL, S3 PUT (must be ok), confirm. Throws on any failure. */
export async function uploadClaimDocument(claimId: string, file: File, documentType: ClaimDocumentType, clientId?: string) {
  const { uploadUrl, s3Key, documentId } = await getClaimDocumentUploadUrl(claimId, {
    contentType: file.type,
    filename: file.name,
    documentType,
  }, clientId);
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status}) for ${file.name}`);
  }
  return confirmClaimDocument(claimId, { s3Key, documentId, documentType, filename: file.name, contentType: file.type }, clientId);
}

/** Public reply on a claim (client on its own claim, or ShipTo). */
export async function replyToClaim(claimId: string, message: string, clientId?: string) {
  return request<{ note: ClaimNote; clientReplied: boolean }>(`/claims/${claimId}/reply${claimClientQs(clientId)}`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

/**
 * Find shipments by order # or tracking #. "Not found" (404) resolves to no
 * matches; any other failure throws, so callers can tell the two apart.
 */
export async function lookupClaimShipment(term: string, clientId?: string): Promise<{ matches: ClaimShipmentMatch[] }> {
  const params = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  try {
    return await request<{ matches: ClaimShipmentMatch[] }>(`/claims/lookup/${encodeURIComponent(term)}${params}`);
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) return { matches: [] };
    throw error;
  }
}

// Claims - Admin endpoints
export async function adminListClaims(params: { status?: ClaimStatus; type?: ClaimType; clientId?: string } = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set('status', params.status);
  if (params.type) searchParams.set('type', params.type);
  if (params.clientId) searchParams.set('clientId', params.clientId);
  const query = searchParams.toString();
  return request<{ claims: ClaimListItem[] }>(query ? `/admin/claims?${query}` : '/admin/claims');
}

/** Optional `?clientId=` hint so the backend can use a keyed claim lookup. */
function claimClientQs(clientId?: string) {
  return clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
}

export async function adminGetClaim(id: string, clientId?: string) {
  return request<ClaimDetail>(`/admin/claims/${id}${claimClientQs(clientId)}`);
}

export async function deleteClaim(id: string, clientId?: string) {
  return request<{ deleted: boolean; claimId: string }>(`/claims/${id}${claimClientQs(clientId)}`, { method: 'DELETE' });
}

export async function deleteClaimNote(claimId: string, field: 'deleteInternalNoteId' | 'deletePublicUpdateId', noteId: string, clientId?: string) {
  return request<{ claimId: string }>(`/claims/${claimId}${claimClientQs(clientId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ [field]: noteId }),
  });
}

// ============ REPLENISHMENT ============

export type CalcMethod = 'average' | 'median';
export type ProductStatus = 'active' | 'new' | 'excluded';
export type Urgency = 'overdue' | 'now' | 'soon' | 'upcoming' | 'ok';

export interface ReplenishmentSettings {
  id: number;
  logiwa_client_id: string;
  calc_method: CalcMethod;
  basis_months: number;
  seasonal: boolean;
  default_horizon_months: number;
  current_month_threshold: number;
  kit_to_stock: boolean;
  use_weighted: boolean;
  include_draft_po: boolean;
  exclude_order_patterns: string[];
  po_prefix: string | null;
  last_sync_at: string | null;
  last_po_sync_at: string | null;
  sync_status: string;
  sync_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReplenishmentReportRow {
  sku: string;
  logiwaProductId: string;
  productName: string;
  imageUrl: string | null;
  supplierName: string | null;
  supplierId: number | null;
  groupId: number | null;
  groupName: string | null;
  status: ProductStatus;
  onHand: number;
  openPO: number;
  inventoryATP: number;
  plannedATP: number;
  demandHistory: Array<{ month: string; quantity: number }>;
  forecastPerMonth: number;
  leadTimeWeeks: number;
  effectiveHorizonMonths: number;
  replenishmentRequired: number;
  replacesSku: string | null;
  inheritedMonths: string[];
  safetyStock: number;
  moq: number;
  moqApplied: boolean;
  stockoutDate: string | null;
  orderByDate: string | null;
  remainingMonths: number;
  urgency: Urgency;
  // Legacy kit pair — API-compat only, not consumed by this UI (kitDemandPortion = kitForecast).
  isKitDerived: boolean;
  kitDemandPortion: number;
  incomingPOs: Array<{ poNumber: string; quantity: number; expectedDate: string | null; status: string }>;
  // Kit-to-Stock (all optional — absent on an older backend, and the UI must
  // degrade to legacy behavior when missing). Monthly units vs one-time
  // backlog units are never blended.
  kitClassification?: 'kit_to_stock' | 'on_demand' | 'unknown' | 'not_kit';
  directForecast?: number;     // units/month from the product's own sales
  kitForecast?: number;        // units/month exploded from kit-to-stock kit sales
  kitBacklogNeed?: number;     // one-time units owed to kit backlog
  assembled?: number | null;   // kit rows: assembled on hand
  capacity?: number | null;    // kit rows: buildable from component stock
  needToAssemble?: number;     // kit rows
  blockedAssembly?: number;    // kit rows: max(0, needToAssemble − capacity)
  constrainingComponentSku?: string | null;
  kitBreakdown?: Array<{
    kitProductId: string;
    kitSku: string;
    kitName: string;
    bomQty: number;
    monthlyContribution: number;
    backlogContribution: number;
  }>;
}

/** True when a row is a kit-to-stock/unknown kit row (assembly concern, never purchasable). */
export function isKitRow(row: Pick<ReplenishmentReportRow, 'kitClassification'>): boolean {
  return row.kitClassification === 'kit_to_stock' || row.kitClassification === 'unknown';
}

/** Logiwa kit type meaning "Kit to Stock" — mirrors backend types/replenishment.ts. */
export const KIT_TO_STOCK_TYPE_ID = 2;

interface DashboardSummary {
  activeSKUs: number;
  inShortage: number;
  runningLow: number;
  urgencyCounts?: Record<Urgency, number>;
  totalUnitsToOrder: number;
  topShortages: Array<{
    sku: string;
    productName: string;
    plannedATP: number;
    replenishmentRequired: number;
    orderByDate: string | null;
    remainingMonths: number;
  }>;
  newProductsCount: number;
  orderTodayCount: number;
  lastSyncAt: string | null;
  syncStatus: string;
}

export interface ReplenishmentSupplier {
  id: number;
  logiwa_client_id: string;
  name: string;
  address: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  lead_time_weeks: number;
  is_default: boolean;
  language: 'en' | 'fr';
  created_at: string;
  updated_at: string;
  product_count?: number;
}

export interface ReplenishmentProduct {
  sku: string;
  logiwa_product_id: string;
  logiwa_client_id: string;
  name: string;
  product_type: string;
  supplier_id: number | null;
  group_id: number | null;
  status: ProductStatus;
  safety_stock: number;
  moq: number;
  replaces_sku: string | null;
  lead_time_weeks: number | null;
  launch_date: string | null;
  forecast_override: number | null;
  notes: string | null;
  image_url: string | null;
  kit_components: Array<{ productCode: string; productName: string; quantity: number }> | null;
  kitTypeId?: number | null; // 2 = kit-to-stock (assembled); other = on-demand; null = unknown
  created_at: string;
  updated_at: string;
  supplierName: string | null;
  groupName: string | null;
  onHand: number | null;
  openPO: number | null;
  plannedATP: number | null;
}

export interface ProductGroup {
  id: number;
  logiwa_client_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  product_count?: number;
  active_count?: number;
  total_on_hand?: number | null;
  total_open_po?: number | null;
  total_available?: number | null;
  total_planned_atp?: number | null;
}

interface SyncStatusResponse {
  last_sync_at: string | null;
  sync_status: string;
  sync_error: string | null;
  pos_expected_today: number;
}

// Helper to append clientId query param for admin client selection
function withClientId(path: string, clientId?: string): string {
  if (!clientId) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}clientId=${encodeURIComponent(clientId)}`;
}

// Settings
export async function getReplenishmentSettings(clientId?: string) {
  return request<ReplenishmentSettings>(withClientId('/replenishment/settings', clientId));
}

export async function updateReplenishmentSettings(data: {
  calc_method?: CalcMethod;
  basis_months?: number;
  seasonal?: boolean;
  default_horizon_months?: number;
  current_month_threshold?: number;
  kit_to_stock?: boolean;
  use_weighted?: boolean;
  include_draft_po?: boolean;
  exclude_order_patterns?: string[];
  po_prefix?: string | null;
  clientId?: string;
}) {
  const { clientId, ...settings } = data;
  return request<ReplenishmentSettings>(withClientId('/replenishment/settings', clientId), {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
}

// Dashboard & Report
export async function getReplenishmentDashboard(clientId?: string) {
  return request<DashboardSummary>(withClientId('/replenishment/dashboard', clientId));
}

export async function getReplenishmentReport(clientId?: string) {
  return request<ReplenishmentReportRow[]>(withClientId('/replenishment/calculate', clientId));
}

export interface ExcludedOrdersPreview {
  matchedCount: number;
  totalCount: number;
  matchedOrders: Array<{
    orderCode: string;
    reference: string;
    date: string;
    itemCount: number;
    totalQty: number;
  }>;
}

export async function getExcludedOrdersPreview(clientId?: string) {
  return request<ExcludedOrdersPreview>(withClientId('/replenishment/excluded-orders-preview', clientId));
}

export async function exportReplenishmentReport(clientId?: string, lang?: string): Promise<Blob> {
  const token = await getRequestToken();
  let url = withClientId('/replenishment/calculate/export', clientId);
  if (lang) url += (url.includes('?') ? '&' : '?') + `lang=${lang}`;
  const response = await fetch(`${API_URL}${url}`, {
    headers: {
      Authorization: token,
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });
  if (response.status === 401) {
    const newToken = await getAuthToken(true);
    const retryResponse = await fetch(`${API_URL}${url}`, {
      headers: {
        Authorization: newToken,
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
    if (!retryResponse.ok) throw new Error('Failed to export report');
    return retryResponse.blob();
  }
  if (!response.ok) throw new Error('Failed to export report');
  return response.blob();
}

// Sync
export async function getReplenishmentSyncStatus(clientId?: string) {
  return request<SyncStatusResponse>(withClientId('/replenishment/sync/status', clientId));
}

export async function triggerReplenishmentSync(clientId?: string) {
  return request<{ message: string; sync_status: string }>(withClientId('/replenishment/sync', clientId), {
    method: 'POST',
  });
}

// Suppliers
export async function listReplenishmentSuppliers(clientId?: string) {
  return request<{ suppliers: ReplenishmentSupplier[] }>(withClientId('/replenishment/suppliers', clientId));
}

export async function updateReplenishmentSupplier(
  id: number,
  data: {
    name?: string;
    address?: string | null;
    contact_name?: string | null;
    email?: string | null;
    phone?: string | null;
    notes?: string | null;
    lead_time_weeks?: number;
    is_default?: boolean;
    language?: 'en' | 'fr';
    clientId?: string;
  }
) {
  const { clientId, ...body } = data;
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return request<ReplenishmentSupplier>(`/replenishment/suppliers/${id}${qs}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// Groups
export async function listProductGroups(clientId?: string) {
  return request<ProductGroup[]>(withClientId('/replenishment/groups', clientId));
}

export async function createProductGroup(data: { name: string; clientId?: string }) {
  const { clientId, ...body } = data;
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return request<ProductGroup>(`/replenishment/groups${qs}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateProductGroup(id: number, data: { name?: string; clientId?: string }) {
  const { clientId, ...body } = data;
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return request<ProductGroup>(`/replenishment/groups/${id}${qs}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteProductGroup(id: number, clientId?: string) {
  return request<{ deleted: boolean }>(withClientId(`/replenishment/groups/${id}`, clientId), {
    method: 'DELETE',
  });
}

// Products
export async function listReplenishmentProducts(params: {
  status?: ProductStatus;
  supplier_id?: number;
  group_id?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  clientId?: string;
} = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set('status', params.status);
  if (params.supplier_id) searchParams.set('supplier_id', String(params.supplier_id));
  if (params.group_id) searchParams.set('group_id', params.group_id);
  if (params.search) searchParams.set('search', params.search);
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.clientId) searchParams.set('clientId', params.clientId);
  const query = searchParams.toString();
  const url = query ? `/replenishment/products?${query}` : '/replenishment/products';
  return request<{ products: ReplenishmentProduct[]; statusCounts: { active: number; new: number; excluded: number }; total: number; page: number; pageSize: number }>(url);
}

export async function fetchAllMatchingProductIds(params: {
  status?: ProductStatus;
  supplier_id?: number;
  group_id?: string;
  search?: string;
  clientId?: string;
} = {}) {
  const searchParams = new URLSearchParams();
  searchParams.set('skusOnly', 'true');
  if (params.status) searchParams.set('status', params.status);
  if (params.supplier_id) searchParams.set('supplier_id', String(params.supplier_id));
  if (params.group_id) searchParams.set('group_id', params.group_id);
  if (params.search) searchParams.set('search', params.search);
  if (params.clientId) searchParams.set('clientId', params.clientId);
  return request<{ productIds: string[] }>(`/replenishment/products?${searchParams.toString()}`);
}

export async function updateReplenishmentProduct(
  logiwaProductId: string,
  data: {
    supplier_id?: number | null;
    group_id?: number | null;
    status?: ProductStatus;
    safety_stock?: number;
    moq?: number;
    replaces_sku?: string | null;
    lead_time_weeks?: number | null;
    launch_date?: string | null;
    forecast_override?: number | null;
    notes?: string | null;
    clientId?: string;
  }
) {
  const { clientId, ...body } = data;
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return request<ReplenishmentProduct>(`/replenishment/products/${encodeURIComponent(logiwaProductId)}${qs}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function bulkUpdateReplenishmentProducts(data: {
  logiwaProductIds: string[];
  updates: {
    supplier_id?: number | null;
    group_id?: number | null;
    status?: ProductStatus;
    safety_stock?: number;
    lead_time_weeks?: number | null;
  };
  clientId?: string;
}) {
  const { clientId, ...body } = data;
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return request<{ updated: number }>(`/replenishment/products/bulk${qs}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// Purchase Orders
export async function listReplenishmentPurchaseOrders(params: {
  status?: string;
  supplier_id?: number;
  clientId?: string;
} = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set('status', params.status);
  if (params.supplier_id) searchParams.set('supplier_id', String(params.supplier_id));
  if (params.clientId) searchParams.set('clientId', params.clientId);
  const query = searchParams.toString();
  const url = query ? `/replenishment/purchase-orders?${query}` : '/replenishment/purchase-orders';
  return request<{ purchaseOrders: Array<{
    id: number;
    po_number: string;
    supplier_id: number;
    supplierName: string;
    status: string;
    lineCount: number;
    totalUnits: number;
    totalReceived: number;
    percentReceived: number | null;
    totalCost: number | null;
    po_type: 'standard' | 'open';
    received_at: string | null;
    completed_at: string | null;
    imported_from_logiwa: boolean;
    logiwa_po_identifier: string | null;
    created_at: string;
    updated_at: string;
  }> }>(url);
}

export async function getReplenishmentPurchaseOrder(id: number, clientId?: string) {
  return request<{
    id: number;
    po_number: string;
    supplier_id: number;
    supplierName: string;
    status: string;
    po_sync_status?: string;
    po_sync_error?: string | null;
    po_sync_checked_at?: string | null;
    // Whether this linked PO's lines may be edited from Arivo right now, and why not.
    linked_edit?: { eligible: boolean; reason?: 'unlinked' | 'flag_off' | 'pending_edit' | 'not_pending' | 'has_receipts' | 'presale' | 'duplicate_product' | 'unverified' };
    notes: string | null;
    expected_delivery_date: string | null;
    logiwa_po_identifier: string | null;
    imported_from_logiwa: boolean;
    created_by: string;
    created_at: string;
    updated_at: string;
    po_type: 'standard' | 'open';
    received_at: string | null;
    completed_at: string | null;
    lines: Array<{
      id: number;
      sku: string;
      logiwa_product_id: string | null;
      product_name: string;
      quantity: number;
      unit_cost: number | null;
      received_quantity: number;
    }>;
    totalUnits: number;
    totalReceived: number;
    totalCost: number | null;
    percentReceived: number | null;
  }>(withClientId(`/replenishment/purchase-orders/${id}`, clientId));
}

export async function createReplenishmentPurchaseOrder(data: {
  po_number?: string;
  supplier_id: number;
  lines: Array<{ sku: string; logiwa_product_id?: string | null; product_name: string; quantity: number; unit_cost?: number | null }>;
  notes?: string;
  expected_delivery_date?: string;
  clientId?: string;
}) {
  const { clientId, ...body } = data;
  return request<{ id: number; po_number: string }>(withClientId('/replenishment/purchase-orders', clientId), {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getNextPOCode(clientId?: string) {
  return request<{ code: string }>(withClientId('/replenishment/purchase-orders/next-code', clientId));
}

export async function updateReplenishmentPurchaseOrder(id: number, data: {
  sync_action?: 'use_logiwa';
  po_number?: string;
  supplier_id?: number;
  status?: string;
  notes?: string | null;
  expected_delivery_date?: string | null;
  lines?: Array<{ sku: string; logiwa_product_id?: string | null; product_name: string; quantity: number; unit_cost?: number | null }>;
  clientId?: string;
}) {
  const { clientId, ...body } = data;
  return request<{ message: string }>(withClientId(`/replenishment/purchase-orders/${id}`, clientId), {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteReplenishmentPurchaseOrder(id: number, clientId?: string) {
  return request<{ message: string }>(withClientId(`/replenishment/purchase-orders/${id}`, clientId), {
    method: 'DELETE',
  });
}

export async function searchPOProducts(q: string, clientId?: string) {
  return request<{ products: Array<{ sku: string; name: string; image_url: string | null; purchase_price: number | null }> }>(
    withClientId(`/replenishment/purchase-orders/product-search?q=${encodeURIComponent(q)}`, clientId)
  );
}

export async function listPODocuments(poId: number, clientId?: string) {
  return request<{ documents: Array<{ identifier: string; documentType: number; documentTypeName: string; fileName: string; fileUrl: string }> }>(
    withClientId(`/replenishment/purchase-orders/${poId}/documents`, clientId)
  );
}

export async function uploadPODocument(poId: number, data: { documentType: number; file: File; clientId?: string }) {
  const { clientId, documentType, file } = data;
  const base = `/replenishment/purchase-orders/${poId}/documents`;
  const s3Key = await stageUpload(withClientId(`${base}/upload-url`, clientId), file);
  return request<{ documentId: string; message: string }>(withClientId(base, clientId), {
    method: 'POST',
    body: JSON.stringify({ documentType, fileName: file.name, s3Key }),
  });
}

export async function getReplenishmentPurchaseOrderPdf(id: number, clientId?: string) {
  const token = await getRequestToken();
  const url = `${API_URL}${withClientId(`/replenishment/purchase-orders/${id}/pdf`, clientId)}`;
  const response = await fetch(url, {
    headers: {
      Authorization: token,
      Accept: 'application/pdf',
    },
  });
  if (!response.ok) throw new Error('PDF download failed');
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  return { url: blobUrl };
}

export async function mergeReplenishmentPurchaseOrders(poIds: number[], clientId?: string) {
  return request<any>(withClientId('/replenishment/purchase-orders/merge', clientId), {
    method: 'POST',
    body: JSON.stringify({ poIds }),
  });
}

export async function sendPurchaseOrderToLogiwa(id: number, clientId?: string) {
  return request<{ logiwa_po_identifier: string }>(withClientId(`/replenishment/purchase-orders/${id}/send-to-logiwa`, clientId), {
    method: 'POST',
  });
}

export async function syncVendorsFromLogiwa(clientId?: string) {
  return request<{ message: string; synced: number; created: number }>(withClientId('/replenishment/suppliers', clientId), {
    method: 'POST',
    body: JSON.stringify({ action: 'sync-from-logiwa' }),
  });
}

// ─── PO line import (file → reviewable lines) ──────────────────

export interface ParsedPOImportLine {
  sku: string;
  product_name: string;
  logiwa_product_id: string | null;
  quantity: number;
  unit_cost: number | null;
  image_url: string | null;
}

export interface POImportParseResult {
  /** SKUs matched to the client's catalog — ready to add to the PO. */
  lines: ParsedPOImportLine[];
  /** SKUs in the file with no catalog match; cannot be ordered as-is. */
  unmatched: Array<{ sku: string; quantity: number; rowNumber: number }>;
  /** Row-level problems (bad quantity, missing columns). */
  errors: string[];
  /** Rows with a blank/zero quantity — expected for the catalog template. */
  skippedNoQuantity: number;
  /** SKUs that appeared twice and were summed into one line. */
  mergedDuplicates: string[];
}

/**
 * Parse a SKU+Quantity file into PO lines. Creates nothing — the caller
 * reviews the result and submits through createReplenishmentPurchaseOrder.
 */
export async function parsePOImportFile(file: File, clientId?: string) {
  const s3Key = await stageUpload(withClientId('/replenishment/purchase-orders/parse-file/upload-url', clientId), file);
  return request<POImportParseResult>(withClientId('/replenishment/purchase-orders/parse-file', clientId), {
    method: 'POST',
    body: JSON.stringify({ fileName: file.name, s3Key }),
  });
}

/**
 * Download a PO import template as a Blob.
 *   mode 'blank'   — SKU | Quantity, for keying off a supplier sheet
 *   mode 'catalog' — every active SKU pre-filled with an empty Quantity column
 */
export async function getPOImportTemplate(
  mode: 'blank' | 'catalog',
  clientId?: string,
  lang?: string,
): Promise<Blob> {
  const token = await getRequestToken();
  let url = withClientId('/replenishment/purchase-orders/import-template', clientId);
  url += (url.includes('?') ? '&' : '?') + `mode=${mode}`;
  if (lang) url += `&lang=${lang}`;

  const accept = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const response = await fetch(`${API_URL}${url}`, { headers: { Authorization: token, Accept: accept } });
  if (response.status === 401) {
    const newToken = await getAuthToken(true);
    const retry = await fetch(`${API_URL}${url}`, { headers: { Authorization: newToken, Accept: accept } });
    if (!retry.ok) throw new Error('Failed to download template');
    return retry.blob();
  }
  if (!response.ok) throw new Error('Failed to download template');
  return response.blob();
}

export async function importLogiwaPurchaseOrders(clientId?: string) {
  return request<{ message: string; imported: number; skipped: number; queued?: boolean; async?: boolean }>(withClientId('/replenishment/purchase-orders/import-logiwa', clientId), {
    method: 'POST',
  });
}

// ── Procurement (standalone PO module) ──────────────────
// Procurement endpoints mirror replenishment PO/supplier endpoints
// but under /procurement/* for clients without full replenishment.

export async function listProcurementPurchaseOrders(params: {
  status?: string;
  supplier_id?: number;
  clientId?: string;
} = {}) {
  const searchParams = new URLSearchParams();
  if (params.status) searchParams.set('status', params.status);
  if (params.supplier_id) searchParams.set('supplier_id', String(params.supplier_id));
  if (params.clientId) searchParams.set('clientId', params.clientId);
  const q = searchParams.toString();
  const url = q ? `/procurement/purchase-orders?${q}` : '/procurement/purchase-orders';
  return request<{ purchaseOrders: Array<{
    id: number;
    po_number: string;
    supplier_id: number;
    supplierName: string;
    status: string;
    lineCount: number;
    totalUnits: number;
    totalReceived: number;
    percentReceived: number | null;
    totalCost: number | null;
    po_type: 'standard' | 'open';
    received_at: string | null;
    completed_at: string | null;
    imported_from_logiwa: boolean;
    logiwa_po_identifier: string | null;
    created_at: string;
    updated_at: string;
  }> }>(url);
}

// ── Products ────────────────────────────────────────────

export interface ProductInventory {
  available: number;
  inStock: number;
  awaitingShipment: number;
  incoming: number;
  availableToBuild: number | null;
}

export interface ProductListItem {
  sku: string;
  // Logiwa's stable product UUID (= shared.products.logiwa_product_id).
  // Inventory merges key on THIS, falling back to SKU only when null —
  // re-code twins share a SKU, so SKU-keyed merges misattribute stock.
  productId: string | null;
  name: string;
  productType: string;
  productTypeName: string | null;
  isActive: boolean;
  barcode: string | null;
  imageUrl: string | null;
  weight: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
  countryOfOrigin: string | null;
  hsTariffCode: string | null;
  customsDescription: string | null;
  declaredValue: number | null;
  packingInstructions: string | null;
  // Logiwa-owned flag, mirrored nightly; toggled via the product PATCH write-back
  isFragile: boolean;
  // Per-SKU alert floor (migration 075). Independent of replenishment safety_stock.
  thresholdWarning: number | null;
  inventory: ProductInventory | null;
}

export interface ProductDetail extends ProductListItem {
  logiwaIdentifier: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface UpdateProductPayload {
  country_of_origin?: string | null;
  hs_tariff_code?: string | null;
  customs_description?: string | null;
  declared_value?: number | null;
  packing_instructions?: string | null;
  is_active?: boolean;
  is_fragile?: boolean;
  threshold_warning?: number | null;
}

/**
 * Catalog only — streams GET /products?page=N pages (no live inventory).
 * Inventory rows come separately from fetchProductsInventory(); merge with
 * mergeProductsInventory(). The split lets the Products page render the list
 * as soon as the catalog arrives (~seconds) while the slower Logiwa inventory
 * report streams in. One merged request used to work, but a big enough
 * catalog (YUL Run: 18k SKUs) exceeds the 6MB Lambda response cap and the
 * APIGW 29s ceiling.
 */
export async function listProductsCatalog(params: { active?: boolean; clientId?: string } = {}) {
  const PAGE_SIZE = 1000;
  const PAGE_CONCURRENCY = 4;
  const base = new URLSearchParams();
  if (params.active === false) base.set('active', 'false');
  if (params.clientId) base.set('clientId', params.clientId);

  type CatalogPage = { products: ProductListItem[]; count: number; page: number; pageSize: number };
  const pageUrl = (page: number) => {
    const s = new URLSearchParams(base);
    s.set('page', String(page));
    s.set('pageSize', String(PAGE_SIZE));
    return `/products?${s.toString()}`;
  };

  const first = await request<CatalogPage>(pageUrl(0)); // page 0 carries the total
  const products = [...first.products];
  const totalPages = Math.ceil(first.count / PAGE_SIZE);
  for (let p = 1; p < totalPages; p += PAGE_CONCURRENCY) {
    const batch = await Promise.all(
      Array.from({ length: Math.min(PAGE_CONCURRENCY, totalPages - p) }, (_, i) => request<CatalogPage>(pageUrl(p + i)))
    );
    for (const b of batch) products.push(...b.products);
  }
  return { products, count: products.length };
}

/** Inventory maps are keyed by productId (Logiwa UUID) with SKU as the
 *  fallback key for rows that lack one — mirrors the merge's own keying. */
export interface ProductsInventoryData {
  atpByKey: Record<string, { available: number; inStock: number; awaitingShipment: number; incoming: number }>;
  kitByKey: Record<string, number>;
  /** True while chunks are still landing (progressive render) or when any
   *  chunk came back partial — surfaced by the UI, never persisted. */
  partial: boolean;
  /** Whether the chunk-0 kit walk provably covered the whole kit list.
   *  null = unknown (backend didn't report — old Lambda during a deploy
   *  window, or degraded chunk) — must NOT be treated as incomplete, or every
   *  kit-owning client sees a false warning banner until the backend ships. */
  kitsComplete: boolean | null;
  /** Non-null once the backend registered a server-side walk for this run. */
  walkId: string | null;
  /** Set after a successful POST finalize (ghost cleanup ran server-side). */
  finalized: boolean;
}

const emptyInventoryData = (): ProductsInventoryData => ({
  atpByKey: {}, kitByKey: {}, partial: true, kitsComplete: null, walkId: null, finalized: false,
});

/** Snapshot read — the <2s fast path. Distinct from the live shape: carries
 *  freshness metadata. (Never-initialized clients just come back with empty
 *  maps; the page's empty-maps-while-fetching check renders the skeleton.) */
export interface ProductsInventorySnapshot {
  data: ProductsInventoryData;
  /** Freshness stamp basis: last complete walk, else oldest row, else null. */
  asOf: string | null;
  /** Only a finalized server-side walk qualifies for the automatic refresh gate. */
  lastCompleteWalkAt: string | null;
  /** True when the snapshot backend/table is unavailable — kill switch: the
   *  page silently falls back to live-walk-only behavior. */
  unavailable: boolean;
}

type SnapshotRowWire = {
  productId: string;
  onHand: number | null;
  openPo: number | null;
  inventoryAtp: number | null;
  plannedAtp: number | null;
  productType: string | null;
  availableToBuild: number | null;
  syncedAt: string;
};

/**
 * Page-walk GET /products/inventory-snapshot. NEVER throws: any failure is
 * the kill-switch case and returns { unavailable: true } so the page keeps
 * today's live-walk behavior.
 */
export async function fetchProductsInventorySnapshot(
  params: { clientId?: string; signal?: AbortSignal; onProgress?: (partial: ProductsInventorySnapshot) => void } = {},
): Promise<ProductsInventorySnapshot> {
  type SnapshotPage = {
    rows: SnapshotRowWire[];
    pageSize: number;
    missing?: boolean;
    meta?: { rowCount: number; lastCompleteWalkAt: string | null; minSyncedAt: string | null };
  };
  const data = emptyInventoryData();
  try {
    // Keyset walk: `after` = last row's productId. The backend paginates by
    // key (not offset) because the live walk mutates the table concurrently.
    let after: string | null = null;
    let meta: SnapshotPage['meta'];
    for (;;) {
      const s = new URLSearchParams();
      if (params.clientId) s.set('clientId', params.clientId);
      if (after) s.set('after', after);
      const res: SnapshotPage = await request<SnapshotPage>(`/products/inventory-snapshot?${s.toString()}`, { signal: params.signal });
      if (res.missing) return { data, asOf: null, lastCompleteWalkAt: null, unavailable: true };
      if (after == null) meta = res.meta;
      for (const row of res.rows) {
        // ATP-sourced rows carry quantities; kit-only rows carry NULLs there
        // and only availableToBuild — never fabricate zeros from them.
        if (row.onHand != null) {
          data.atpByKey[row.productId] = {
            available: row.inventoryAtp ?? 0,
            inStock: row.onHand,
            awaitingShipment: row.onHand - (row.inventoryAtp ?? 0),
            incoming: row.openPo ?? 0,
          };
        }
        if (row.availableToBuild != null) data.kitByKey[row.productId] = row.availableToBuild;
      }
      if (res.rows.length < res.pageSize) break;
      after = res.rows[res.rows.length - 1]!.productId;
      // Large catalogs need several sequential keyset pages (~0.5s each) —
      // surface each page as it lands so the first 5000 rows render early
      // instead of waiting for the whole read. Abort-guarded like the walk.
      if (params.onProgress && !params.signal?.aborted) {
        params.onProgress({
          data: { ...data, atpByKey: { ...data.atpByKey }, kitByKey: { ...data.kitByKey } },
          asOf: meta?.lastCompleteWalkAt ?? meta?.minSyncedAt ?? null,
          lastCompleteWalkAt: meta?.lastCompleteWalkAt ?? null,
          unavailable: false,
        });
      }
    }
    data.partial = false; // snapshot data is a complete read of what's persisted
    return {
      data,
      asOf: meta?.lastCompleteWalkAt ?? meta?.minSyncedAt ?? null,
      lastCompleteWalkAt: meta?.lastCompleteWalkAt ?? null,
      unavailable: false,
    };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') throw err;
    console.warn('Inventory snapshot unavailable — falling back to live walk', err);
    return { data: emptyInventoryData(), asOf: null, lastCompleteWalkAt: null, unavailable: true };
  }
}

type InventoryChunk = {
  atp: Array<{ sku: string; productId: string | null; available: number; inStock: number; awaitingShipment: number; incoming: number }>;
  kits: Array<{ sku: string; productId: string | null; availableToBuild: number }>;
  partial: boolean;
  kitsComplete?: boolean;
  walkId: string | null;
  persisted: boolean;
  nextCursor: number | null;
  totalChunks: number | null;
};

/**
 * Live inventory for the whole catalog, streamed from
 * GET /products/inventory-report?cursor=N (cursor = Logiwa ATP page index).
 * The first chunk reports totalChunks (fetch the rest in parallel) and issues
 * the server-side walkId echoed on every later chunk. Chunks merge
 * progressively via onProgress; after a zero-partial, all-persisted walk the
 * server-verified finalize runs and the returned data is marked finalized.
 * The frontend never advances snapshot freshness itself — callers background-
 * refetch the snapshot after finalize.
 */
export async function fetchProductsInventory(
  params: { clientId?: string; signal?: AbortSignal; onProgress?: (data: ProductsInventoryData) => void } = {},
): Promise<ProductsInventoryData> {
  const base = new URLSearchParams();
  if (params.clientId) base.set('clientId', params.clientId);

  const data = emptyInventoryData();
  let allPersisted = true;
  let anyPartial = false;

  const chunkUrl = (cursor: number, walkId: string | null) => {
    const s = new URLSearchParams(base);
    s.set('cursor', String(cursor));
    if (walkId) s.set('walkId', walkId);
    return `/products/inventory-report?${s.toString()}`;
  };
  const keyOf = (row: { sku: string; productId: string | null }) => row.productId ?? row.sku;
  const applyChunk = (chunk: InventoryChunk) => {
    for (const row of chunk.atp) {
      const { available, inStock, awaitingShipment, incoming } = row;
      data.atpByKey[keyOf(row)] = { available, inStock, awaitingShipment, incoming };
    }
    for (const kit of chunk.kits) data.kitByKey[keyOf(kit)] = kit.availableToBuild;
    if (chunk.partial) anyPartial = true;
    if (!chunk.persisted) allPersisted = false;
    data.partial = true; // stays true until the walk finishes
    // Progressive merge: hand the caller a snapshot-in-time copy. Guarded by
    // the abort signal so a superseded walk (client switch) can't publish.
    if (params.onProgress && !params.signal?.aborted) {
      params.onProgress({ ...data, atpByKey: { ...data.atpByKey }, kitByKey: { ...data.kitByKey } });
    }
  };

  const firstChunk = await request<InventoryChunk>(chunkUrl(0, null), { signal: params.signal });
  data.walkId = firstChunk.walkId ?? null;
  data.kitsComplete = typeof firstChunk.kitsComplete === 'boolean' ? firstChunk.kitsComplete : null;
  applyChunk(firstChunk);
  if (firstChunk.nextCursor !== null) {
    const stride = firstChunk.nextCursor; // pages per chunk, as decided by the backend
    if (firstChunk.totalChunks !== null && firstChunk.totalChunks > 1) {
      const INV_CONCURRENCY = 3;
      const cursors = Array.from({ length: firstChunk.totalChunks - 1 }, (_, i) => stride * (i + 1));
      for (let i = 0; i < cursors.length; i += INV_CONCURRENCY) {
        const batch = await Promise.all(
          cursors.slice(i, i + INV_CONCURRENCY).map((c) => request<InventoryChunk>(chunkUrl(c, data.walkId), { signal: params.signal })),
        );
        batch.forEach(applyChunk);
      }
    } else {
      let cursor: number | null = firstChunk.nextCursor;
      while (cursor !== null) {
        const chunk: InventoryChunk = await request<InventoryChunk>(chunkUrl(cursor, data.walkId), { signal: params.signal });
        applyChunk(chunk);
        cursor = chunk.nextCursor;
      }
    }
  }

  data.partial = anyPartial;
  // Server-verified cleanup: only attempted when this walk saw no partial
  // chunk and every chunk persisted — and the backend re-verifies from its
  // own record regardless. Failure here never fails the walk.
  if (data.walkId && !anyPartial && allPersisted) {
    try {
      // clientId rides the QUERY PARAM (like every chunk request): tenant
      // resolution happens before body parsing, and without the override an
      // admin/multi-store session resolves its own primary client — the walk
      // lookup would miss and the walk would never finalize. (The body schema
      // is strict, so the body can't carry it.)
      const res = await request<{ outcome: string }>(`/products/inventory-snapshot/finalize?${base.toString()}`, {
        method: 'POST',
        body: JSON.stringify({ walkId: data.walkId }),
        signal: params.signal,
      });
      data.finalized = res.outcome === 'finalized' || res.outcome === 'already_finalized';
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') throw err;
      console.warn('Inventory walk finalize failed (non-fatal)', err);
    }
  }
  return data;
}

/** Merge catalog rows with inventory (snapshot, live, or combined). Keyed by
 *  productId with SKU fallback — SKU alone misattributes re-code twins. */
export function mergeProductsInventory(products: ProductListItem[], inv: ProductsInventoryData): ProductListItem[] {
  return products.map((p) => {
    // SKU fallback ONLY when the catalog row has no productId (key === sku
    // then). A non-null productId that misses the map must resolve to "no
    // data": a SKU-keyed row can belong to a re-code twin, and falling back
    // would misattribute that twin's stock — the exact bug this key prevents.
    const key = p.productId ?? p.sku;
    const atp = inv.atpByKey[key];
    const availableToBuild = p.productType === 'Kit' ? (inv.kitByKey[key] ?? null) : null;
    const inventory = atp
      ? { ...atp, availableToBuild }
      : (availableToBuild !== null
        ? { available: 0, inStock: 0, awaitingShipment: 0, incoming: 0, availableToBuild }
        : null);
    return { ...p, inventory };
  });
}

/** Catalog + inventory in one call (kept for callers that want the old shape). */
export async function listProducts(params: { active?: boolean; clientId?: string } = {}) {
  const [catalog, inventory] = await Promise.all([
    listProductsCatalog(params),
    fetchProductsInventory({ clientId: params.clientId }),
  ]);
  const merged = mergeProductsInventory(catalog.products, inventory);
  return { products: merged, count: merged.length };
}

export async function updateProduct(sku: string, data: UpdateProductPayload, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string; sku: string; logiwaSync?: 'ok' | 'failed'; warning?: string }>(
    `/products/${encodeURIComponent(sku)}${qs}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
  );
}

export interface BulkUpdateProductsPayload {
  skus: string[];
  updates: {
    country_of_origin?: string | null;
    hs_tariff_code?: string | null;
    customs_description?: string | null;
    declared_value?: number | null;
    is_active?: boolean;
    threshold_warning?: number | null;
  };
}

export async function bulkUpdateProducts(data: BulkUpdateProductsPayload, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string; count: number }>(`/products/bulk${qs}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function syncProducts(clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string; count: number }>(`/products/sync${qs}`, {
    method: 'POST',
  });
}

export async function exportProducts(type: 'products' | 'inventory', clientId?: string, lang?: string): Promise<Blob> {
  const token = await getRequestToken();
  const params = new URLSearchParams();
  if (clientId) params.set('clientId', clientId);
  if (lang) params.set('lang', lang);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_URL}/products/export/${type}${qs}`, {
    headers: {
      Authorization: token,
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });
  if (!response.ok) throw new Error('Export failed');
  return response.blob();
}

// ── Async xlsx exports (background-generate → S3 → presigned URL) ──
// Large exports (products inventory: 60s+ ATP; replenishment: growing) can't be
// generated inside the 29s API Gateway window, so we start a job, poll for it,
// and download from the presigned URL when ready.

export interface ExportJobStatus {
  status: 'ready' | 'pending' | 'error';
  url?: string;
  message?: string;
}

function exportQs(clientId?: string, lang?: string): string {
  const params = new URLSearchParams();
  if (clientId) params.set('clientId', clientId);
  if (lang) params.set('lang', lang);
  return params.toString() ? `?${params.toString()}` : '';
}

export async function startProductsInventoryExport(clientId?: string, lang?: string): Promise<{ jobId: string }> {
  return request<{ jobId: string }>(`/products/export/inventory/start${exportQs(clientId, lang)}`, { method: 'POST' });
}

export async function getProductsExportStatus(jobId: string, clientId?: string): Promise<ExportJobStatus> {
  const params = new URLSearchParams({ jobId });
  if (clientId) params.set('clientId', clientId);
  return request<ExportJobStatus>(`/products/export/status?${params.toString()}`);
}

export async function startReplenishmentExport(clientId?: string, lang?: string): Promise<{ jobId: string }> {
  return request<{ jobId: string }>(`/replenishment/calculate/export/start${exportQs(clientId, lang)}`, { method: 'POST' });
}

export async function getReplenishmentExportStatus(jobId: string, clientId?: string): Promise<ExportJobStatus> {
  const params = new URLSearchParams({ jobId });
  if (clientId) params.set('clientId', clientId);
  return request<ExportJobStatus>(`/replenishment/calculate/export/status?${params.toString()}`);
}

/**
 * Poll an export job until ready, then trigger the browser download from the
 * presigned URL (its Content-Disposition forces the Save-As). Throws on error
 * or timeout.
 */
export async function pollAndDownloadExport(
  jobId: string,
  statusFn: (jobId: string) => Promise<ExportJobStatus>,
  opts: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<void> {
  const intervalMs = opts.intervalMs ?? 2500;
  const deadline = Date.now() + (opts.timeoutMs ?? 180000);
  for (;;) {
    const res = await statusFn(jobId);
    if (res.status === 'ready' && res.url) {
      const a = document.createElement('a');
      a.href = res.url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    if (res.status === 'error') throw new Error(res.message || 'Export failed');
    if (Date.now() > deadline) throw new Error('Export timed out');
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

// Threshold-warning subscription is a per-user boolean on the user profile
// (receiveThresholdReport on AdminUser / via PUT /admin/users/{id}). The
// per-client settings table + GET/PUT /reports/threshold/settings endpoints
// were removed when we simplified to the receivePONotifications-style toggle.

// ── AI-Assisted HS Code Lookup ─────────────────────────

export interface ClientPortrait {
  client_name: string;
  primary_categories: string[];
  typical_materials: string[];
  market_type: string;
  country_of_origin_hints: string[];
  hs_chapter_hints: string[];
  notes: string;
}

export interface HSCodeSubheading {
  hs_code: string;
  description: string;
}

export interface HSCodeSuggestion {
  hs_code: string;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  official_description?: string;
  customs_description?: string;
  country_of_origin?: string;
  validated: boolean;
  subheadings?: HSCodeSubheading[];
}

export async function buildClientPortrait(websiteUrl: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ portrait: ClientPortrait }>(`/products/portrait/build${qs}`, {
    method: 'POST',
    body: JSON.stringify({ websiteUrl }),
  });
}

export async function getClientPortrait(clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{
    portrait: ClientPortrait | null;
    websiteUrl?: string;
    builtAt?: string;
    builtBy?: string;
  }>(`/products/portrait${qs}`);
}

export async function updateClientPortrait(portrait: ClientPortrait, websiteUrl?: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string }>(`/products/portrait${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ portrait, websiteUrl }),
  });
}

export interface SimilarSku {
  sku: string;
  name: string;
}

export async function suggestHSCode(productName: string, productType?: string, clientId?: string, options?: { sku?: string; findSimilar?: boolean }) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ suggestion: HSCodeSuggestion; similarSkus?: SimilarSku[] }>(`/products/hs-suggest${qs}`, {
    method: 'POST',
    body: JSON.stringify({ productName, productType, ...options }),
  });
}

export async function confirmHSCode(
  data: {
    productDescription: string;
    sku?: string | null;
    hsCode: string;
    hsDescription?: string | null;
    source: 'ai+human' | 'manual';
    confidence?: 'high' | 'medium' | 'low' | null;
  },
  clientId?: string,
) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string }>(`/products/hs-confirm${qs}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================================
// Orders Module
// ============================================================

export interface OrderListItem {
  identifier: string;
  code: string;
  channelOrderNumber: string;
  status: string;
  orderDate: string;
  shipDate: string;
  customerName: string;
  companyName: string;
  carrier: string;
  carrierId: number | null;
  /** Last-mile sub-carrier (e.g. "Intelcom Standard" when carrier is STALLIONEXPRESSV4). */
  shippingOptionName: string | null;
  orderType: string;
  trackingNumber: string;
  trackingTag: string | null;
  trackingSlug: string | null;
  itemCount: number;
  totalQuantity: number;
  trackingHealth: string | null;
  staleReason: { days: string; province: string; threshold: string } | null;
  shippingCost: number | null;
  shortageRisk: boolean;
  /** Logiwa cancel reason, e.g. 'Order Merge' / 'Unmerged Order'. */
  cancellationReason: string | null;
  /** Surviving Merged Order code when this order was cancelled by a merge. */
  mergedIntoCode: string | null;
  /** Originals merged INTO this order (empty when not a merge master), with
   *  their store order numbers — the identifier people actually search for. */
  mergedFrom?: Array<{ code: string; channelOrderNumber: string | null }>;
}

export interface OrderDetail extends OrderListItem {
  packingInstructions: string;
  items: Array<{
    sku: string;
    name: string;
    quantity: number;
    shippedQuantity: number;
  }>;
  recipient: {
    name: string;
    company: string;
    address1: string;
    address2: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    phone: string;
    email: string;
  };
  warehouseName: string;
  channelName: string;
  source?: string;               // 'manual' | 'import' — only manual orders are editable
  holdCount: number;
  holdUntil: string | null;
}

/**
 * Editable-Orders V1 gate (mirrors backend editability.ts): only orders created
 * in Arivo ('manual'), in a pre-fulfillment status, may have their number/content
 * edited. Keep in sync with backend EDIT_ALLOWED_STATUSES.
 */
export const ORDER_EDIT_STATUSES = ['open', 'none', 'new'];
export function canEditOrder(o: { source?: string; status?: string }): boolean {
  return (o.source ?? '').trim().toLowerCase() === 'manual'
    && ORDER_EDIT_STATUSES.includes((o.status ?? '').trim().toLowerCase());
}

export async function listOrders(params: {
  page?: number;
  pageSize?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  status?: string;
  clientId?: string;
  trackingFilter?: string;
  sortKey?: string;
  sortDir?: string;
} = {}) {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  if (params.search) searchParams.set('search', params.search);
  if (params.status) searchParams.set('status', params.status);
  if (params.clientId) searchParams.set('clientId', params.clientId);
  if (params.trackingFilter) searchParams.set('trackingFilter', params.trackingFilter);
  if (params.sortKey) searchParams.set('sortKey', params.sortKey);
  if (params.sortDir) searchParams.set('sortDir', params.sortDir);
  const query = searchParams.toString();
  return request<{ orders: OrderListItem[]; totalCount: number }>(query ? `/orders?${query}` : '/orders');
}

export async function getOrderDetail(code: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ order: OrderDetail }>(`/orders/${encodeURIComponent(code)}${qs}`);
}

export interface TrackingIssueStats {
  exceptionCount: number;
  staleCount: number;
  unregisteredCount: number;
  totalIssues: number;
}

export async function getOrderStats(params: { dateFrom?: string; dateTo?: string; clientId?: string }) {
  const searchParams = new URLSearchParams();
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  if (params.clientId) searchParams.set('clientId', params.clientId);
  return request<{ stats: Record<string, number>; trackingStats: TrackingIssueStats; shortageCount?: number; shortageRiskCount?: number }>(`/orders/stats?${searchParams.toString()}`);
}

export async function backfillOrders(clientId?: string) {
  return request<{ message: string; totalUpserted?: number; tracked?: number }>('/orders/backfill', {
    method: 'POST',
    body: JSON.stringify(clientId ? { clientId } : {}),
  });
}

export async function exportOrdersCsv(params: { dateFrom?: string; dateTo?: string; search?: string; status?: string; trackingFilter?: string; clientId?: string; lang?: string }): Promise<Blob> {
  const token = await getRequestToken();
  const searchParams = new URLSearchParams();
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  if (params.search) searchParams.set('search', params.search);
  if (params.status) searchParams.set('status', params.status);
  if (params.trackingFilter) searchParams.set('trackingFilter', params.trackingFilter);
  if (params.clientId) searchParams.set('clientId', params.clientId);
  if (params.lang) searchParams.set('lang', params.lang);
  const res = await fetch(`${API_URL}/orders/export?${searchParams.toString()}`, {
    headers: {
      Authorization: token,
      // Required: API Gateway matches request Accept against binaryMediaTypes
      // to decide whether to base64-decode the lambda response. Without this
      // header the xlsx body comes through as raw base64 text.
      Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });
  if (!res.ok) throw new Error('Export failed');
  return res.blob();
}

export function getOrderExportUrl(params: { dateFrom?: string; dateTo?: string; search?: string; status?: string; clientId?: string; lang?: string }) {
  const searchParams = new URLSearchParams();
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);
  if (params.search) searchParams.set('search', params.search);
  if (params.status) searchParams.set('status', params.status);
  if (params.clientId) searchParams.set('clientId', params.clientId);
  if (params.lang) searchParams.set('lang', params.lang);
  return `/orders/export?${searchParams.toString()}`;
}

// ────────────────────────────────────────────────────────────
// Manual Order Creation Wizard (POST /orders/manual, GET /products/search, GET /products/inventory)
// ────────────────────────────────────────────────────────────

export interface ManualOrderAddress {
  country: string;      // 2-letter ISO
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface ManualOrderLineItem {
  sku: string;
  quantity: number;
  unitPrice: number;
}

export interface ManualOrderRequest {
  orderCode?: string;           // omit for auto-generated
  shippingMode?: 'ship' | 'pickup'; // defaults to 'ship' on the backend
  firstName: string;
  lastName: string;
  email?: string;               // required for ship, omitted for pickup
  phone?: string;               // required for ship, omitted for pickup
  companyName?: string;
  address?: ManualOrderAddress; // required for ship, omitted for pickup
  packingInstructions?: string;
  saveRecipient?: boolean;      // mirror recipient into the saved-customer picker
  lineItems: ManualOrderLineItem[];
}

export async function createManualOrder(data: ManualOrderRequest, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ code: string; message: string }>(`/orders/manual${qs}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Read-only preview of the next SO number for the caller's effective client.
 * Used by the wizard to pre-fill the order code input. Does not consume
 * the counter — the real allocation happens at create time.
 */
export async function getNextOrderCode(clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ code: string }>(`/orders/next-code${qs}`);
}

export interface RecipientSearchResult {
  customerIdentifier: string;
  addressIdentifier: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  companyName: string | null;
  isDefaultAddress: boolean;
  address: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

export async function searchOrderRecipients(q: string, clientId?: string, limit = 20): Promise<RecipientSearchResult[]> {
  const trimmed = q.trim();
  if (trimmed.length < 2) return [];
  const params = new URLSearchParams({ q: trimmed, limit: String(limit) });
  if (clientId) params.set('clientId', clientId);
  const res = await request<{ recipients: RecipientSearchResult[] }>(`/orders/customers/search?${params.toString()}`);
  return res.recipients;
}

export interface ProductSearchResult {
  sku: string;
  name: string;
  imageUrl: string | null;
  salePrice: number | null;
  purchasePrice?: number | null;
  // Customs prefill for the US-shipment customs line picker (may be missing
  // on product records that haven't been customs-classified yet)
  hsCode?: string | null;
  customsDescription?: string | null;
  countryOfOrigin?: string | null;
  declaredValue?: number | null;
}

export async function searchProducts(q: string, limit: number = 10, clientId?: string) {
  const params = new URLSearchParams({ q, limit: String(limit) });
  if (clientId) params.set('clientId', clientId);
  return request<{ items: ProductSearchResult[] }>(`/products/search?${params.toString()}`);
}

export async function searchProductsForPO(q: string, clientId?: string): Promise<ProductSearchResult[]> {
  const res = await searchPOProducts(q, clientId);
  return res.products.map(p => ({
    sku: p.sku,
    name: p.name,
    imageUrl: p.image_url,
    salePrice: null,
    purchasePrice: p.purchase_price,
  }));
}

export interface ProductInventoryItem {
  sku: string;
  atp: number | null;  // null = Logiwa didn't return it (unknown); 0 = out of stock
}

export async function getProductInventory(skus: string[], clientId?: string) {
  if (skus.length === 0) return { items: [] };
  const params = new URLSearchParams({ skus: skus.join(',') });
  if (clientId) params.set('clientId', clientId);
  return request<{ items: ProductInventoryItem[] }>(`/products/inventory?${params.toString()}`);
}

// ────────────────────────────────────────────────────────────

export async function holdOrderAction(code: string, clientId?: string, holdUntil?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  const body = holdUntil ? JSON.stringify({ holdUntil }) : undefined;
  return request<{ message: string; holdCount: number; holdUntil: string | null }>(`/orders/${encodeURIComponent(code)}/hold${qs}`, { method: 'POST', body });
}

export async function releaseOrderAction(code: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string }>(`/orders/${encodeURIComponent(code)}/release${qs}`, { method: 'POST' });
}

export interface OrderDocument {
  identifier: string;
  documentType: number;
  documentTypeName: string;
  fileName: string;
  fileUrl: string;
}

export const DOCUMENT_TYPES = [
  { value: 1, key: 'carrierLabel' },
  { value: 2, key: 'giftNote' },
  { value: 3, key: 'packingInstruction' },
  { value: 4, key: 'packingDocument' },
  { value: 5, key: 'billOfLading' },
  { value: 6, key: 'external' },
  { value: 7, key: 'invoice' },
  { value: 8, key: 'kitInstructions' },
  { value: 9, key: 'retailer' },
] as const;

export async function getOrderDocuments(code: string, clientId?: string) {
  return request<{ documents: OrderDocument[] }>(withClientId(`/orders/${encodeURIComponent(code)}/documents`, clientId));
}

export interface OrderSerial {
  shipmentOrderCode: string;
  sku: string;
  productName: string | null;
  primarySerialNumber: string | null;
  secondarySerialNumber: string | null;
  lotBatchNumber: string | null;
  shipmentDateTime: string | null;
  trackingNumber: string | null;
  carrierCode: string | null;
  lpNumber: string | null;
  parentLPNumber: string | null;
  expiryDate: string | null;
  warehouseCode: string | null;
}

export async function getOrderSerials(code: string, clientId?: string) {
  return request<{ serials: OrderSerial[] }>(withClientId(`/orders/${encodeURIComponent(code)}/serials`, clientId));
}

/**
 * Stage a file in S3 for an API call: ask `uploadUrlPath` for a presigned PUT,
 * send the file straight to S3, and return the s3Key for the real request.
 * Sending files base64 in the API body hit Lambda's 6 MB payload cap, which
 * the browser only saw as "Failed to fetch". Server side: utils/staged-upload.ts.
 */
async function stageUpload(uploadUrlPath: string, file: File, extraBody?: Record<string, string>): Promise<string> {
  const { uploadUrl, s3Key, contentType } = await request<{ uploadUrl: string; s3Key: string; contentType: string }>(
    uploadUrlPath,
    { method: 'POST', body: JSON.stringify({ ...extraBody, size: file.size, contentType: file.type }) },
  );
  const put = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
  if (!put.ok) {
    // S3 errors are XML: <Error><Code>SignatureDoesNotMatch</Code>…
    const s3Code = (await put.text().catch(() => '')).match(/<Code>([^<]+)<\/Code>/)?.[1];
    throw new Error(`Upload failed (${put.status}${s3Code ? ` ${s3Code}` : ''})`);
  }
  return s3Key;
}

export async function uploadOrderDocument(code: string, file: File, documentType: number, clientId?: string) {
  const base = `/orders/${encodeURIComponent(code)}/documents`;
  const s3Key = await stageUpload(withClientId(`${base}/upload-url`, clientId), file);
  return request<{ documentId: string }>(withClientId(base, clientId), {
    method: 'POST',
    body: JSON.stringify({ documentType, fileName: file.name, s3Key }),
  });
}

export async function deleteOrderDocument(code: string, documentId: string, clientId?: string) {
  return request<{ deleted: boolean }>(withClientId(`/orders/${encodeURIComponent(code)}/documents/${encodeURIComponent(documentId)}`, clientId), {
    method: 'DELETE',
  });
}

export async function cancelOrderAction(code: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string }>(`/orders/${encodeURIComponent(code)}/cancel${qs}`, { method: 'POST' });
}

export async function shipOrderAction(code: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string }>(`/orders/${encodeURIComponent(code)}/ship${qs}`, { method: 'POST' });
}

export async function expressToggleAction(code: string, clientId: string | undefined, enable: boolean) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string; carrier: string; carrierId: number; isExpress: boolean }>(`/orders/${encodeURIComponent(code)}/express${qs}`, { method: 'POST', body: JSON.stringify({ enable }) });
}

export async function pickupToggleAction(code: string, clientId: string | undefined, enable: boolean) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string; carrier: string; carrierId: number; isPickup: boolean }>(`/orders/${encodeURIComponent(code)}/pickup${qs}`, { method: 'POST', body: JSON.stringify({ enable }) });
}

export async function partialToggleAction(code: string, clientId: string | undefined, enable: boolean) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string; orderType: string; isPartial: boolean }>(`/orders/${encodeURIComponent(code)}/partial${qs}`, { method: 'POST', body: JSON.stringify({ enable }) });
}

export async function updateOrderPackingInstructions(code: string, packingInstructions: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string }>(`/orders/${encodeURIComponent(code)}/packing-instructions${qs}`, {
    method: 'PATCH',
    body: JSON.stringify({ packingInstructions }),
  });
}

export async function updateOrderAddress(code: string, address: {
  name: string;
  company: string;
  address1: string;
  address2?: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  phone?: string;
  email?: string;
}, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string }>(`/orders/${encodeURIComponent(code)}/address${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ address }),
  });
}

// ── Editable Orders V1 (manual, pre-fulfillment orders only) ──

/** Replace an order's line items (full list — send the complete desired set). */
export async function updateOrderContent(
  code: string,
  items: Array<{ sku: string; quantity: number; packType?: string }>,
  clientId?: string,
) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string; items: Array<{ sku: string; name: string; quantity: number; shippedQuantity: number }> }>(
    `/orders/${encodeURIComponent(code)}/content${qs}`,
    { method: 'PATCH', body: JSON.stringify({ items }) },
  );
}

/** Rename an order's number (Logiwa SO code). Returns the new number. */
export async function renameOrderNumber(code: string, orderNumber: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ message: string; orderNumber: string }>(
    `/orders/${encodeURIComponent(code)}/number${qs}`,
    { method: 'PATCH', body: JSON.stringify({ orderNumber }) },
  );
}

export interface TransactionHistoryItem {
  transactionType: string;
  timestamp: string;
  user: string;
  sku: string;
  productName: string;
  quantity: number;
}

export async function getOrderHistory(code: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ picking: TransactionHistoryItem[]; packing: TransactionHistoryItem[] }>(`/orders/${encodeURIComponent(code)}/history${qs}`);
}

// Shortage types
/**
 * Shortage truth: Logiwa's shortage figure is from its last allocation attempt.
 * Backend joins it to the live inventory snapshot. All fields below are
 * optional/additive — older responses simply lack them.
 */
export type ShortageStatus = 'waiting_on_stock' | 'stock_arrived' | 'unknown';

export interface ShortageItem {
  status?: ShortageStatus;
  /** Real missing units (Logiwa's figure when status is 'unknown'). */
  trueShortfall?: number;
  nextPoNumber?: string | null;
  nextPoEta?: string | null;
  sku: string;
  productName: string;
  quantityNeeded: number;
  quantityShort: number;
  freeQuantity: number;
  onHandQuantity: number;
  damagedQuantity: number;
  shortageReason: string;
  orderAge: number | null;
  lastAllocationAttempt: string | null;
}

export interface ShortageSkuSummary {
  status?: ShortageStatus;
  trueShortfall?: number;
  nextPoNumber?: string | null;
  nextPoEta?: string | null;
  sku: string;
  productName: string;
  totalQuantityShort: number;
  totalQuantityNeeded: number;
  orderCount: number;
  shortageReason: string;
}

export interface ShortageLineItem {
  status?: ShortageStatus;
  trueShortfall?: number;
  orderCode: string;
  channelOrderNumber: string | null;
  orderDate: string | null;
  orderType: string | null;
  sku: string;
  productName: string;
  quantityNeeded: number;
  quantityShort: number;
  freeQuantity: number;
  onHandQuantity: number;
  damagedQuantity: number;
  shortageReason: string;
  orderAge: number | null;
  lastAllocationAttempt: string | null;
}

export interface ShortageReport {
  stats: {
    totalLines: number; totalSkus: number; totalOrders: number;
    /** Real missing units (stock-arrived SKUs excluded). */
    totalUnits?: number;
    /** Orders still genuinely waiting on stock. */
    ordersHeld?: number;
    /** Orders whose short SKUs are all covered by stock now. */
    stockArrivedOrders?: number;
  };
  skuSummary: ShortageSkuSummary[];
  lines: ShortageLineItem[];
}

/** Admin only: orders with stock available but still in Shortage in Logiwa. */
export interface StockArrivedOrder {
  clientId: string;
  clientName: string | null;
  orderCode: string;
  channelOrderNumber: string | null;
  orderAge: number | null;
  lastAllocationAttempt: string | null;
  lines: Array<{ sku: string; productName: string | null; quantityNeeded: number; logiwaQuantityShort: number }>;
}

export async function getStockArrivedOrders() {
  return request<{ orders: StockArrivedOrder[] }>('/orders/shortages?stockArrived=1');
}

export async function getOrderShortages(code: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ shortages: ShortageItem[]; cached?: boolean }>(`/orders/${encodeURIComponent(code)}/shortages${qs}`);
}

// ── Shortage Risk (predicted, not yet confirmed) ──
//
// Distinct from Shortages: a "shortage risk" is computed by `shortage-sync`
// from Logiwa's negative-ATP signal — open/on-hold orders containing SKUs
// that are oversold. Backed by the shortage_risk_orders Aurora table.

export interface ShortageRiskSkuRow {
  sku: string;
  productName: string;
  inventoryAtp: number;
  totalDemand: number;
  totalShort: number;
  orderCount: number;
}

export interface ShortageRiskSummary {
  stats: { totalOrders: number; totalSkus: number };
  skuSummary: ShortageRiskSkuRow[];
}

export interface ShortageRiskItem {
  sku: string;
  quantityNeeded: number;
  quantityAvailable: number;
  quantityShort: number;
  calculatedAt: string;
}

export async function getShortageRiskSummary(clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<ShortageRiskSummary>(`/orders/shortage-risk${qs}`);
}

export async function getOrderShortageRisk(code: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ risks: ShortageRiskItem[] }>(`/orders/${encodeURIComponent(code)}/shortage-risk${qs}`);
}

export async function getShortageReport(clientId?: string, search?: string, sku?: string) {
  const params = new URLSearchParams();
  if (clientId) params.set('clientId', clientId);
  if (search) params.set('search', search);
  if (sku) params.set('sku', sku);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return request<ShortageReport>(`/orders/shortages${qs}`);
}

// ── Order Tracking ──

export interface OrderTrackingCheckpoint {
  tag: string;
  subtag: string;
  subtagMessage: string | null;
  checkpointTime: string;
  city: string | null;
  state: string | null;
  location: string | null;
  message: string | null;
}

export interface OrderTrackingRegistration {
  trackingNumber: string;
  slug: string;
  aftershipId: string | null;
  tag: string | null;
  subtag: string | null;
  registeredAt: string;
  checkpoints: OrderTrackingCheckpoint[];
}

export async function getOrderTracking(code: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ tracking: OrderTrackingRegistration[] }>(`/orders/${encodeURIComponent(code)}/tracking${qs}`);
}

/** One box: its tracking number (null = Logiwa gave none) and what shipped in it. */
export interface OrderPackage {
  trackingNumber: string | null;
  items: Array<{ sku: string; name: string; quantity: number }>;
}

export async function getOrderPackages(code: string, clientId?: string) {
  const qs = clientId ? `?clientId=${clientId}` : '';
  return request<{ packages: OrderPackage[]; contentsUnavailable: boolean }>(`/orders/${encodeURIComponent(code)}/packages${qs}`);
}

export async function backfillOrderTracking(clientId: string, daysBack: number = 7) {
  return request<{ message: string; registered: number; skipped: number; failed: number; total: number }>(
    '/orders/tracking-backfill',
    { method: 'POST', body: JSON.stringify({ clientId, daysBack }) },
  );
}

// ── Exception Pokes ──────────────────────────────────────

export interface ExceptionPokeSummary {
  id: number;
  orderCode: string;
  trackingNumber: string;
  carrierSlug: string;
  exceptionTag: string;
  pokeType: 'carrier' | 'shipto' | 'dismiss';
  status: string;
  escalationLevel: number;
  emailSubject: string | null;
  emailTo: string | null;
  pokedBy: string;
  sentAt: string | null;
  dismissedAt: string | null;
  createdAt: string;
}

export interface PokeCooldown {
  canPokeCarrier: boolean;
  canPokeShipTo: boolean;
  carrierCooldownUntil?: string;
  shipToCooldownUntil?: string;
  isDismissed: boolean;
}

export interface PokeDraftResponse {
  pokeId: number;
  emailSubject: string;
  emailBody: string;
  sentTo: string;
  sentFrom: string;
  isPortal: boolean;
  portalUrl?: string;
  status: string;
  cooldownUntil?: string;
}

export interface MerchantContext {
  vip?: boolean;
  customerContacted?: boolean;
  timeSensitive?: string;
  notes?: string;
}

export async function getOrderPokes(code: string, trackingNumber?: string) {
  const params = new URLSearchParams();
  if (trackingNumber) params.set('trackingNumber', trackingNumber);
  const qs = params.toString();
  return request<{ pokes: ExceptionPokeSummary[]; cooldown?: PokeCooldown }>(
    `/orders/${encodeURIComponent(code)}/pokes${qs ? `?${qs}` : ''}`
  );
}

export async function pokeAction(
  code: string,
  body: {
    action: 'draft' | 'send' | 'dismiss';
    pokeType?: 'carrier' | 'shipto';
    trackingNumber?: string;
    carrierSlug?: string;
    exceptionTag?: string;
    exceptionSubtag?: string;
    exceptionMessage?: string;
    daysStuck?: number;
    aftershipTrackingId?: string;
    lastCheckpointLocation?: string;
    lastCheckpointDate?: string;
    shipDate?: string;
    originCity?: string;
    destinationCity?: string;
    destinationRegion?: string;
    clientIdentifier?: string;
    merchantContext?: MerchantContext;
    pokeId?: number;
    editedSubject?: string;
    editedBody?: string;
    emailSubject?: string;
    emailBody?: string;
    to?: string;
    sentTo?: string;
    replyTo?: string;
  }
) {
  return request<PokeDraftResponse>(
    `/orders/${encodeURIComponent(code)}/poke`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// ── Admin: Carrier Contacts ──────────────────────────────

export interface CarrierContact {
  id: number;
  slug: string;
  carrier_display_name: string;
  department: string;
  contact_email: string;
  contact_phone: string | null;
  contact_method: 'email' | 'phone' | 'portal';
  portal_url: string | null;
  escalation_level: number;
  account_number: string | null;
  ai_instructions: string | null;
  email_language: string;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export async function listCarrierContacts(activeOnly = false) {
  const qs = activeOnly ? '?activeOnly=true' : '';
  return request<{ contacts: CarrierContact[] }>(`/admin/carrier-contacts${qs}`);
}

export async function createCarrierContact(data: Partial<CarrierContact>) {
  return request<{ contact: CarrierContact }>('/admin/carrier-contacts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCarrierContact(id: number, data: Partial<CarrierContact>) {
  return request<{ contact: CarrierContact }>(`/admin/carrier-contacts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCarrierContact(id: number) {
  return request<{ deleted: boolean }>(`/admin/carrier-contacts/${id}`, {
    method: 'DELETE',
  });
}

// ── Dashboard ────────────────────────────────────────────

/** A dashboard section whose query failed (the section itself is then null, never 0/[]). */
export type DashboardSectionKey =
  | 'shipped' | 'pipeline' | 'monthly' | 'recentShipments' | 'shortages'
  | 'claims' | 'tracking' | 'receiving' | 'replenishment' | 'outstandingPOs' | 'turnover'
  | 'activityPO' | 'activityRma' | 'activityClaims';

export type DashboardActivityEvent =
  | { type: 'reception'; at: string; poNumber: string; discrepancyCount: number; clientId: string }
  | { type: 'poCreated'; at: string; poNumber: string; poId: number }
  | { type: 'rmaCreated' | 'rmaReceived'; at: string; rmaNumber: string; rmaId: string }
  | { type: 'claimOpened' | 'claimUpdated'; at: string; claimNumber: string; claimId: string }
  | { type: 'shippedSummary'; at: null; count: number; week: 'this' | 'last' };

/**
 * Contract: `null` (or a missing field from an older API) means "do not show
 * this", never "zero". A section the client has no module for is null and is NOT
 * in `sectionErrors`; a section whose query failed is null AND is listed there.
 */
export interface DashboardPayload {
  kpis: {
    shippedToday: number | null;
    shippedThisWeek: number | null;
    shippedThisMonth: number | null;
    shortages: number | null;
    activeClaims: number | null;
  };
  pipeline: Record<string, number> | null;
  onHold: number | null;
  monthlyVolume: Array<{ month: string; count: number }> | null;
  /** Units shipped per month, same 12 months as monthlyVolume. */
  unitsByMonth?: Array<{ month: string; units: number }> | null;
  /** Last 5 shipped orders, newest first. */
  recentShipments?: Array<{ code: string; shippedAt: string; units: number; carrier: string | null }> | null;
  activityEvents?: DashboardActivityEvent[] | null;
  /** Orders being picked or packed right now. */
  inProgressCount?: number | null;
  /** Signed, short-lived URL of the client's gray app logo. */
  logoUrl?: string | null;
  /** Stock in the building today / monthly shipped volume (live, not the billing month). */
  liveMonthsOfSupply?: number | null;
  sectionErrors?: DashboardSectionKey[];
  /** Enabled modules for the viewed client. */
  enabledModules?: string[];
  tracking: {
    enabled: boolean;
    inTransit: number;
    delivered: number;
    exceptions: number;
    /** null when the stale-count query failed. */
    staleCount: number | null;
    total: number;
    tagCounts?: Array<{ tag: string; count: number }>;
  } | null;
  turnover: {
    tier: string;
    multiplier: number;
    monthsOfSupply: number | null;
    inStockSkuCount: number;
    storedCuft: number;
    shippedCuft: number;
    slowestSkus: Array<{
      sku: string;
      name: string;
      monthsOfSupply: number;
    }>;
    worstOffenders?: Array<{
      sku: string;
      name: string;
      wasteScore: number;
      storedCuft: number;
      monthsOfSupply: number | null;
    }>;
  } | null;
  receiving: {
    activeBatches: Array<{
      poNumber: string;
      vendorName: string;
      startedAt: string;
      elapsedMinutes: number;
    }>;
    /** null when the outstanding-POs query failed. */
    outstandingPOs: Array<{
      id: number;
      poNumber: string;
      supplierName: string;
      expectedDate: string | null;
      status: string;
      lineCount: number;
      totalUnits: number;
    }> | null;
  };
  replenishment: {
    enabled: boolean;
    urgentItems: Array<{
      sku: string;
      productName: string;
      daysUntilStockout: number | null;
      remainingMonths: number | null;
      replenishmentRequired: number;
      plannedATP: number;
      orderByDate: string | null;
      urgency?: string;
    }>;
    urgencyCounts: Record<string, number>;
    horizonMonths?: number;
    totalUnitsToOrder?: number;
  } | null;
  claims: {
    total: number;
    new: number;
    waitingOnClient?: number;
    filedWithCarrier: number;
    resolved: number;
  } | null;
  shortagesBreakdown: {
    stats: {
      totalUnits: number;
      skuCount: number;
      ordersHeld: number;
      oldestDays: number;
      noPoSkuCount: number;
      /** Orders whose short SKUs are all covered by stock now (optional: older API). */
      stockArrivedOrders?: number;
    };
    topSkus: Array<{
      sku: string;
      productName: string;
      /** Real missing quantity (0 when stock arrived). */
      quantityShort: number;
      status?: ShortageStatus;
      trueShortfall?: number;
      logiwaQuantityShort?: number;
      orderCount: number;
      nextPoNumber: string | null;
      nextPoEta: string | null;
    }>;
  } | null;
}

export async function getDashboard(clientId?: string) {
  return request<DashboardPayload>(withClientId('/orders/dashboard', clientId));
}

// Warehousing

export interface WarehousingCurrentMetrics {
  tier: string;
  tierColor: string;
  multiplier: number;
  isNewClient: boolean;
  totalMonthCount: number;
  monthsOfSupply: number | null;
  rotationsPerYear: number | null;
  avgStoredCuft: number;
  avgShippedCuft: number;
  productCount: number;
  productsWithoutVolumeCount: number;
  lastCalculated: string | null;
}

export interface WarehousingOffender {
  sku: string;
  name: string;
  storedCuft: number;
  shippedCuft: number;
  storedQty: number;
  shippedQty: number;
  currentQty: number;
  ratio: number | null;
  isDead: boolean;
  wasteScore: number;
  pctStorage: number;
  monthCount: number;
}

export interface WarehousingHistoryMonth {
  yearMonth: string;
  storedCuft: number;
  shippedCuft: number;
  ratio: number | null;
  tier: string;
  tierColor: string;
}

export interface WarehousingSnapshotInfo {
  /** YYYY-MM-DD of the most recent daily inventory snapshot. */
  date: string;
  /** ISO timestamp the snapshot was captured (nightly), if known. */
  capturedAt: string | null;
}

/** One rung of the storage tier ladder. `name` is the French name ("Sain"); map it with tierNameKey(). */
export interface WarehousingTierThreshold {
  name: string;
  minMonths: number;
  maxMonths: number | null;
  multiplier: number;
}

export interface WarehousingPayload {
  currentMetrics: WarehousingCurrentMetrics;
  /** Tier ladder from Compass. null/absent on an older Compass: the page hides the ladder. */
  tierThresholds?: WarehousingTierThreshold[] | null;
  /** Cubic feet in the building on the latest daily snapshot (live). */
  liveStoredCuft?: number | null;
  /** Months of supply TODAY (live). The billing-month figure is currentMetrics.monthsOfSupply. */
  liveMonthsOfSupply?: number | null;
  worstOffenders: WarehousingOffender[];
  totalStoredCuft: number;
  history: WarehousingHistoryMonth[];
  /**
   * Live-inventory stamp. When present, worst-offenders `currentQty`/`storedCuft`
   * reflect this daily snapshot (live on-hand) rather than Compass's monthly
   * rollup, and the $/mo column is a static-inventory estimate as of this date.
   */
  asOfSnapshot?: WarehousingSnapshotInfo | null;
}

export async function getWarehousing(clientId?: string) {
  return request<WarehousingPayload | { warehousing: null }>(
    withClientId('/orders/warehousing', clientId)
  );
}

// ============ RECEPTION (client-facing LIVE receiving) ============
// Mirrors the Compass reception contract proxied by GET /orders/reception.
// Per-PO; the internal batch/"lot" concept is never exposed. Variance only once
// a PO is complete. See backend/src/handlers/reception/types.ts.

export type ReceptionStage = 'arrived' | 'receiving' | 'inspection' | 'putaway' | 'finalizing' | 'complete';
export type ReceptionReportStatus = 'none' | 'generating' | 'available' | 'failed';

export interface ReceptionTimelineEntry {
  stage: ReceptionStage;
  at: string;
}
export interface ReceptionNote {
  body: string;
  createdAt: string;
}
export interface ReceptionPhoto {
  id: number;
  url: string;
  damaged: boolean;
  note: string;
  takenAt: string;
}
export interface ReceptionVarianceLine {
  sku: string;
  name: string;
  ordered: number;
  received: number;
  status: string;
}
export interface ReceptionVariance {
  status: string;
  lines: ReceptionVarianceLine[];
  priorReceipts: number;
  computedAt: string | null;
}
export interface ReceptionPO {
  poNumber: string;
  vendorName: string | null;
  stage: ReceptionStage;
  stageEnteredAt: string | null;
  arrivedAt: string | null;
  closedAt: string | null;
  /** Labor actually logged on this receipt (billing basis + any live timer) — NOT wall-clock. */
  laborMinutes: number;
  /** Workers with a timer running right now (0 = nobody actively handling it). */
  activeWorkers: number;
  sharedWithPoCount: number;
  totalReceipts: number;
  isFollowUp: boolean;
  pallets: number;
  boxes: number;
  timeline: ReceptionTimelineEntry[];
  clientNotes: ReceptionNote[];
  photos: ReceptionPhoto[];
  variance: ReceptionVariance | null;
  reportStatus: ReceptionReportStatus;
}
export interface ReceptionPayload {
  hasReceivingHistory: boolean;
  inProgress: ReceptionPO[];
  recentlyCompleted: ReceptionPO[];
}
export type ReceptionReportResult =
  | { status: 'ready'; _reportUrl: string; filename?: string; expiresDays: number }
  | { status: 'generating' }
  | { status: 'failed' }
  | { status: 'unavailable' };

export async function getReception(clientId?: string) {
  return request<ReceptionPayload>(withClientId('/orders/reception', clientId));
}

export async function getReceptionReport(
  poNumber: string,
  lang: 'fr' | 'en',
  clientId?: string,
): Promise<ReceptionReportResult> {
  const base = `/orders/reception/${encodeURIComponent(poNumber)}/report?lang=${lang}`;
  try {
    return await request<ReceptionReportResult>(withClientId(base, clientId));
  } catch (e) {
    // The Arivo proxy 404s when Compass has no report for this PO — surface it as
    // a poll status rather than an exception.
    if (e instanceof ApiError && e.statusCode === 404) return { status: 'unavailable' };
    throw e;
  }
}

// Help
export async function searchHelp(question: string, language: string) {
  return request<{ answer: string; articleIds: string[] }>('/help/search', {
    method: 'POST',
    body: JSON.stringify({ question, language }),
  });
}

// ============================================================
// Order Import
// ============================================================

export interface ImportTemplate {
  id: string;
  name: string;
  nameEn: string;
  nameFr: string;
  autoDetect: boolean;
  hasDownload: boolean;
}

export interface ImportJobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  totalOrders: number;
  processedOrders: number;
  successCount: number;
  errorCount: number;
  /** Orders already in Logiwa (duplicate code) — benign, kept out of errors. */
  skippedCount: number;
  errors: Array<{ orderCode: string; message: string }>;
  /** Parse-time data-quality notices (incomplete addresses, unparseable files) — never failures. */
  warnings?: Array<{ orderCode: string; message: string }>;
  templateId: string | null;
  fileNames: string[];
  createdAt: string;
}

export interface ImportUploadResponse {
  jobId: string;
  totalOrders: number;
  /** Raw parser format string (e.g. "logiwa-simplified") — internal use. */
  detectedTemplate: string | null;
  /** Resolved template id from the registry (e.g. "isoacoustics-qb"). */
  detectedTemplateId?: string | null;
  /** User-facing template name (e.g. "IsoAcoustics — QuickBooks Import"). */
  detectedTemplateName?: string | null;
  parseErrors?: string[];
  message: string;
}

export async function getImportTemplates(clientId?: string) {
  return request<{ templates: ImportTemplate[] }>(withClientId('/orders/import/templates', clientId));
}

export async function uploadOrderImport(
  files: File[],
  clientIdentifier?: string,
  templateId?: string,
) {
  const staged = await Promise.all(files.map(async (f) => ({
    name: f.name,
    s3Key: await stageUpload(withClientId('/orders/import/upload-url', clientIdentifier), f),
  })));
  return request<ImportUploadResponse>('/orders/import', {
    method: 'POST',
    body: JSON.stringify({
      clientIdentifier,
      files: staged,
      templateId: templateId || 'auto',
    }),
  });
}

export async function pollImportJob(jobId: string) {
  return request<ImportJobStatus>(`/orders/import/${jobId}`);
}

// The route sits behind the Cognito authorizer, so it can't be a plain <a href>
// (a browser navigation carries no Authorization header — API Gateway answers
// {"message":"Unauthorized"} before the Lambda ever runs). Fetch it with the
// token and hand the caller a Blob to save.
export async function downloadImportTemplate(clientId?: string): Promise<Blob> {
  const token = await getRequestToken();

  const response = await fetch(
    `${API_URL}${withClientId('/orders/import/template-download', clientId)}`,
    {
      headers: {
        Authorization: token,
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to download template');
  }

  return response.blob();
}

export interface POPresaleState {
  version?: 1 | 2;
  mixed?: boolean;
  enabledLines?: number;
  eligibleLines?: number;
  id: number;
  poId: string;
  enabled: boolean;
  revision: number;
  arrivedAt: string | null;
  status: 'off' | 'pending' | 'active' | 'stopped' | 'error' | 'withdrawn';
  quantity: number;
  lastError: string | null;
}
export interface POPresaleResponse {
  items: POPresaleState[];
  readiness: { ready: boolean; reason: string | null };
}
export function getPOPresale(ids: number[], clientId?: string) {
  return request<POPresaleResponse>(withClientId(`/replenishment/purchase-orders/presale?poIds=${ids.join(',')}`, clientId));
}

export interface POPresaleLine {
  lineId: string; productId: string; sku: string; name?: string;
  orderedUnits: number; receivedUnits: number; outstandingUnits: number;
  enabled: boolean; revision: number; eligible: boolean; reason: string | null; contribution: number;
  /** Set when Logiwa dropped the line (every PO edit reissues line UUIDs); such rows are history, not controls. */
  retiredAt?: string | null;
  intentLost?: boolean; withdrawn?: boolean;
}
export interface POPresaleLinesResponse {
  version: 1 | 2; poId: string; revision: number; sourceHash: string | null; observedAt?: string | null;
  readiness: { ready: boolean; reason: string | null };
  items: POPresaleLine[]; status: string; lastError: string | null; stale?: boolean;
  isolated?: boolean; quarantinedProducts?: string[];
}
export function getPOPresaleLines(id: number, clientId?: string, signal?: AbortSignal) {
  return request<POPresaleLinesResponse>(withClientId(`/replenishment/purchase-orders/${id}/presale/lines`, clientId), { signal });
}
export function setPOPresaleLines(id: number, body: {
  changes: Array<{ lineId: string; enabled: boolean; expectedRevision: number }>;
  expectedRevision: number; sourceHash: string; requestId: string;
}, clientId?: string, signal?: AbortSignal) {
  return request<POPresaleLinesResponse>(withClientId(`/replenishment/purchase-orders/${id}/presale/lines`, clientId), { method: 'PUT', body: JSON.stringify(body), signal });
}

// ─── Return decisions (Retourneur, proxied to Compass via /orders/return-decisions) ───
export type ReturnOption = 'stock_N' | 'stock_E' | 'rebut' | 'to_client' | 'pickup' | 'reship' | 'inspect';
export type ReturnPolicyAction = 'sell' | 'damaged' | 'dispose' | 'to_client' | 'reship';
export interface ReturnHoldSummary {
  id: number; code: string; state: string; is_parcel: boolean; parked_at: string | null;
  decision: string | null; decision_grade: string | null; decided_at: string | null; decided_by: string | null;
  sku: string | null; product_name: string | null; note: string | null; order_code: string | null; customer_name: string | null;
}
export interface ReturnHoldList { client: string | null; waiting: ReturnHoldSummary[]; decided: ReturnHoldSummary[] }
export interface ReturnFinding {
  key: string; label_fr: string; label_en: string; type: string; answer: unknown; flagged: boolean;
  /** `choice` checks only: the option the worker picked, in words. */
  answer_label_fr?: string | null; answer_label_en?: string | null;
}
export interface ReturnHoldEvent { at: string; event: 'received' | 'held' | 'decided' | 'done'; by?: string | null; tracking?: string | null }
export interface ReturnHoldDetail {
  id: number; code: string; state: string; is_parcel: boolean; sku: string | null; product_name: string | null;
  order_code: string | null; customer_name: string | null; note: string | null; parked_at: string | null;
  decision: { decision: string; grade: string | null; at: string | null; by: string | null } | null;
  findings: ReturnFinding[];
  photos: Array<{ url: string }>;
  options: ReturnOption[];
  history: ReturnHoldEvent[];
}
export interface ReturnPolicy {
  decision_owner: 'CLIENT' | 'INTERNAL'; bounce_days: number; allowed_actions: ReturnPolicyAction[]; actions: ReturnPolicyAction[];
}

export async function getReturnHolds(clientId?: string) {
  return request<ReturnHoldList>(withClientId('/orders/return-decisions', clientId));
}
export async function getReturnHold(id: string, clientId?: string) {
  return request<ReturnHoldDetail>(withClientId(`/orders/return-decisions/${encodeURIComponent(id)}`, clientId));
}
export async function decideReturnHold(id: string, body: { option: ReturnOption; note?: string; address?: string }, clientId?: string) {
  return request<{ hold_id: number; code: string; option: ReturnOption; todo_kind: string | null }>(
    withClientId(`/orders/return-decisions/${encodeURIComponent(id)}/decision`, clientId),
    { method: 'POST', body: JSON.stringify(body) },
  );
}
export async function getReturnPolicy(clientId?: string) {
  return request<ReturnPolicy>(withClientId('/orders/return-decisions/policy', clientId));
}
export async function updateReturnPolicy(
  body: { decision_owner: 'CLIENT' | 'INTERNAL'; allowed_actions: ReturnPolicyAction[]; bounce_days: number },
  clientId: string,
) {
  return request<ReturnPolicy>(withClientId('/orders/return-decisions/policy', clientId), { method: 'PUT', body: JSON.stringify(body) });
}

// ── Étiquettes (merchant product labels, proxied to Compass) ─────────────────
// The Compass label engine renders; Arivo passes the tenant. Errors keep
// Compass's code + params (ApiError.code / .details), translated in
// components/labels/labelMessages.ts.

export type LabelStatus = 'ok' | 'a_verifier' | 'bloque';
export type LabelField = 'product_name' | 'size' | 'colour' | 'price' | 'category';
export type LabelFieldValue = string | number | null;

export interface LabelTemplate {
  id: string;
  name: string;
  fields: LabelField[];
  /** Label roll: 'dymo' | 'mm89x29' | 'mm90x25' … */
  stock: string | null;
}

export interface LabelIssue {
  code: string;
  field?: string;
  blocking?: boolean;
  params?: Record<string, unknown>;
}

export interface LabelRow {
  sku: string;
  productIdentifier: string | null;
  qtyOrdered?: number | null;
  qtyPrint: number;
  fields: Partial<Record<LabelField, LabelFieldValue>>;
  sourceFields: Partial<Record<LabelField, LabelFieldValue>>;
  printFields: Partial<Record<LabelField, LabelFieldValue>>;
  fieldSources: Partial<Record<LabelField, string>>;
  barcode: string | null;
  status: LabelStatus;
  issues: LabelIssue[];
  correction: { version: number; updatedAt?: string } | null;
}

export interface LabelGroup {
  style: { key: string; label: string };
  rows: LabelRow[];
}

export interface LabelLines {
  template: LabelTemplate;
  groups: LabelGroup[];
  /** SKUs the template never labels (gift cards, packaging…), not shown as rows. */
  skipped?: string[];
  truncated?: boolean;
}

/** Where the lines come from: the three « Étiquettes » shortcuts. */
export type LabelSourceBody =
  | { source: 'products'; items: Array<{ productIdentifier: string }> }
  | { source: 'order'; orderCode: string; qtyMode: 'ordered' | 'one' }
  | { source: 'po'; poId: string; qtyMode: 'full' | 'one' };

export interface LabelFileResult {
  file: { data: string; contentType: string; filename: string };
  labelCount: number | null;
  blockedCount: number | null;
  styleCount: number | null;
}

export function getLabelTemplates(clientId?: string) {
  return request<{ templates: LabelTemplate[] }>(withClientId('/orders/labels/templates', clientId));
}

export function getLabelLines(body: LabelSourceBody & { templateId?: string }, clientId?: string) {
  return request<LabelLines>(withClientId('/orders/labels/lines', clientId), { method: 'POST', body: JSON.stringify(body) });
}

export function previewLabel(
  body: { templateId?: string; sku: string; productIdentifier: string; edits: Partial<Record<LabelField, LabelFieldValue>>; sourceFields: Partial<Record<LabelField, LabelFieldValue>> },
  clientId?: string,
  signal?: AbortSignal,
) {
  return request<LabelFileResult>(withClientId('/orders/labels/preview', clientId), { method: 'POST', body: JSON.stringify(body), signal });
}

export function saveLabelProduct(
  body: { templateId?: string; productIdentifier: string; sku: string; fields: Partial<Record<LabelField, LabelFieldValue>>; expectedVersion: number | null },
  clientId?: string,
) {
  return request<{ row: LabelRow }>(withClientId('/orders/labels/product', clientId), { method: 'PUT', body: JSON.stringify(body) });
}

export function resetLabelProduct(
  body: { templateId?: string; productIdentifier: string; sku: string; expectedVersion: number | null },
  clientId?: string,
) {
  return request<{ row: LabelRow }>(withClientId('/orders/labels/product', clientId), { method: 'DELETE', body: JSON.stringify(body) });
}

/**
 * Print. Safe through request(): a 401 retry only happens when API Gateway's
 * authorizer refused the call (the Lambda never ran, nothing was printed), and
 * network-failure retries are GET-only.
 */
export function printLabels(body: LabelSourceBody & { templateId?: string; quantities?: Record<string, number> }, clientId?: string) {
  return request<LabelFileResult>(withClientId('/orders/labels/print', clientId), { method: 'POST', body: JSON.stringify(body) });
}

/** A label file (base64 from Compass) as a Blob for download or display. */
export function labelFileToBlob(file: LabelFileResult['file']): Blob {
  const bin = atob(file.data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: file.contentType });
}
